import { DateTime } from "luxon";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { Fragment, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Plus, Minus, Download, X } from "lucide-react";
import type { SearchableSelectOption } from "../components/ui/SearchableSelect/SearchableSelect";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Input } from "../components/ui/Input/Input";
import inputStyles from "../components/ui/Input/Input.module.css";
import { Select } from "../components/ui/Select/Select";
import { FormField } from "../components/ui/FormField/FormField";
import { Modal } from "../components/ui/Modal/Modal";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import { useVatGroups } from "../hooks/use-vat-groups";
import {
	useCreateOffer,
	useUpdateOffer,
	useOfferDetail,
	usePendingOfferAttachments,
	useUploadPendingOfferAttachments,
	useDeletePendingOfferAttachment,
	pendingOfferAttachmentDownloadUrl,
	type PendingOfferAttachment,
} from "../hooks/use-offers";
import { FileDropzone } from "../components/ui/FileDropzone/FileDropzone";
import { ShippingAddressPicker } from "../components/ui/ShippingAddressPicker/ShippingAddressPicker";
import {
	useOrderDetail,
	useCreateOrder,
	useUpdateOrder,
	useOrderAttachments,
	useUploadOrderAttachments,
	useDeleteOrderAttachment,
	orderAttachmentDownloadUrl,
	type OrderAttachment,
} from "../hooks/use-orders";
import { useStockItemUnits } from "../hooks/use-stock-items";
import { useFormDraft } from "../hooks/use-form-draft";
import { useDelegation } from "../hooks/use-delegation";
import { api } from "../lib/api";
import { useToast } from "../lib/use-toast";
import { parsePanelLabel } from "../lib/panel-lines";
import { formatAmount } from "../lib/date";
import { deriveGtip } from "@crm/shared/gtip";

const CURRENCY_OPTIONS = [
	{ value: "TL", label: "TL" },
	{ value: "USD", label: "USD" },
	{ value: "EUR", label: "EUR" },
	{ value: "GBP", label: "GBP" },
];
import styles from "./OfferNew.module.css";
import { StockReservationPanel } from "../components/StockReservationPanel/StockReservationPanel";
import { useLineStockAvailability } from "../hooks/use-stock-reservations";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/offers/new",
	component: withPermission(OfferNewPage, "offers", "create"),
});

interface LineItem {
	lineNumber: number;
	itemCode: string;
	itemDescription: string;
	displayDescription: string;
	quantity: string;
	unit: string;
	unitPrice: string;
	givenUnitPrice: string;
	vatRate: string;
	vatGroup: string;
	discountPercent: string;
	notes: string;
	factorySpecialNotes: string;
	generalNotes: string;
	stockItemId: string;
	showInPdf: boolean;
	showPriceInPdf: boolean;
	showDiscountInPdf: boolean;
	sendDatasheetInEmail: boolean;
	isCustomDesign: boolean;
	lineDetail: string;
	priceListModel: string;
	commitmentValue: string;
	commitmentUnit: string;
	alternativeGroup: number;
	isSection: boolean;
	sectionItems: SectionItem[];
	warehouseId: string;
	atsDescription: string;
	tmsDescription: string;
}

type OrderDetailLine = NonNullable<ReturnType<typeof useOrderDetail>["data"]>["lines"][number];

const ORDER_LINE_DEFAULTS = { warehouseId: "", atsDescription: "", tmsDescription: "" };

const ORDER_STATUS_LABELS: Record<string, string> = {
	draft: "Açık (Taslak)",
	confirmed: "Açık",
	partially_delivered: "Açık (Kısmi Sevk)",
	delivered: "Açık (Sevk Edildi)",
	invoiced: "Açık (Faturalandı)",
	closed: "Kapalı",
	cancelled: "İptal Edildi",
};

interface SeparatePanel {
	stockItemId: string;
	itemCode: string;
	itemDescription: string;
	unit: string;
	vatGroup: string;
	unitPrice: number;
}

/** Ayrı faturalanan pano grubunda seçilebilecek SAP kalemlerinin stok kategorisi. */
const PANEL_ITEM_CATEGORY: Record<string, string> = {
	ATS: "ats",
	"Change Over ATS": "changeover",
	CB: "cb",
};

// Eski belgelerdeki "ATS - Yok" / "CB - Yok" pano satırları açılışta atlanır, yeniden yazılmaz.
const PANEL_NONE_OPTION = "Yok";

interface SeparatePanelLoad extends SeparatePanel {
	group: string;
	option: string;
	qty: number;
}

/** Ayrı faturalanan pano satırı: SAP kalemli ve priceComponents'inde `separate` işaretli pano etiketi taşır. */
function parseSeparatePanelLine(line: {
	stockItemId: string | null;
	itemCode: string | null;
	itemDescription: string | null;
	unit: string | null;
	vatGroup: string | null;
	unitPrice: string | null;
	quantity: string | null;
	priceComponents: { label: string; price: number; separate?: boolean }[] | null;
}): SeparatePanelLoad | null {
	if (!line.stockItemId) return null;
	const component = (line.priceComponents ?? []).find((c) => c.separate);
	const parsed = component ? parsePanelLabel(component.label) : null;
	if (!parsed) return null;
	return {
		group: parsed.group,
		option: parsed.option,
		qty: Number(line.quantity) || parsed.qty,
		stockItemId: line.stockItemId,
		itemCode: line.itemCode ?? "",
		itemDescription: line.itemDescription ?? "",
		unit: line.unit ?? "",
		vatGroup: line.vatGroup ?? "",
		unitPrice: Number(line.unitPrice) || 0,
	};
}

function separatePanelState(
	byParent: Record<number, Record<string, SeparatePanelLoad>>,
): Record<number, Record<string, SeparatePanel>> {
	const result: Record<number, Record<string, SeparatePanel>> = {};
	for (const [idx, groups] of Object.entries(byParent)) {
		result[Number(idx)] = Object.fromEntries(
			Object.entries(groups).map(([group, { group: _g, option: _o, qty: _q, ...panel }]) => [
				group,
				panel,
			]),
		);
	}
	return result;
}

interface SectionItem {
	name: string;
	quantity: string;
	unitPrice: string;
}

/** Teklifte sık kullanılan hazır başlıklar; kullanıcı serbest metin de yazabilir. */
const SECTION_PRESETS = [
	"SENKRONİZASYON VE KONTROL SİSTEMİ",
	"TMŞ-TRANSFER SİSTEMİ",
	"ŞARTNAME EKSTRALARI",
];

/** Başlık satırının birim fiyatı alt kalemlerin toplamıdır. */
const sectionUnitPrice = (items: SectionItem[]): number =>
	items.reduce((sum, i) => sum + (Number(i.quantity) || 0) * (Number(i.unitPrice) || 0), 0);

/** "offerOrderDeliveryType" alanına yazılan sabit değer: teklif stoktan teslim edilecekse Termin Süresi yerine kullanılır. */
const STOCK_DELIVERY_TYPE = "stock";

// Bazı SAP ürün açıklamalarında marka adı eksik geçiyor (örn. "...KABİNLİ ALTERNATÖRLÜ..."
// yerine "...STAMFORD ALTERNATÖRLÜ..."); ürün adının başındaki model kısaltmasından
// (GJC700-ST gibi) markayı bulmak için yedek (fallback) eşleme.
const ALTERNATOR_SUFFIX_BRANDS: Record<string, string> = {
	LS: "LEROY SOMER",
	ST: "STAMFORD",
	MAR: "MARANELLO",
};

const TERM_KEYS = [
	"commissioning",
	"assembly",
	"vat",
	"quality",
	"validity",
	"warranty",
	"payment",
	"delivery",
	"paymentMethod",
] as const;
type TermKey = (typeof TERM_KEYS)[number];

function OfferNewPage() {
	const search = route.useSearch() as {
		companyId?: string;
		contactPersonId?: string;
		copyFrom?: string;
	};
	return <OfferFormPage initialSearch={search} copyFromId={search.copyFrom} />;
}


// "Isparta Enerji" ile "ISPARTA ENERJİ SAN. VE TİC. A.Ş." eşleşsin diye noktalama ve büyük/küçük harf farkı atılır.
function normalizeCompanyName(name: string): string {
	return name
		.toLocaleUpperCase("tr")
		.replace(/[ÇĞİÖŞÜ]/g, (c) => ({ Ç: "C", Ğ: "G", İ: "I", Ö: "O", Ş: "S", Ü: "U" })[c] ?? c)
		.replace(/[^0-9A-Z]+/g, " ")
		.trim();
}

export function OfferFormPage({
	editId,
	copyFromId,
	initialSearch,
	orderId,
	copyOrderId,
}: {
	editId?: string;
	copyFromId?: string | undefined;
	initialSearch?: { companyId?: string; contactPersonId?: string };
	orderId?: string;
	copyOrderId?: string | undefined;
}) {
	const { t, i18n } = useTranslation();
	const { showToast } = useToast();
	const navigate = useNavigate();
	const createOffer = useCreateOffer();
	const updateOffer = useUpdateOffer();
	const { data: unitsData } = useStockItemUnits();
	const unitOptions = (unitsData?.units ?? []).map((u) => ({ value: u, label: u }));
	// Sipariş kopyası düzenleme formuyla açılır, kaydedince yeni sipariş oluşturur.
	const isOrderCopy = !orderId && !!copyOrderId;
	const isOrder = !!orderId || isOrderCopy;
	const isEdit = !!editId || isOrder;
	const detail = useOfferDetail(editId ?? copyFromId ?? "");
	const orderDetail = useOrderDetail(orderId ?? copyOrderId ?? "");
	const createOrder = useCreateOrder();
	const updateOrder = useUpdateOrder();
	const [orderHeader, setOrderHeader] = useState({
		sourceOfferId: "",
		status: "confirmed",
		orderDate: "",
		deliveryDate: "",
		shippingAddress: "",
		shippingAddressId: "",
		startupDescription: "",
		installationDescription: "",
	});
	const orderAttachments = useOrderAttachments(orderId ?? null);
	const uploadOrderAttachments = useUploadOrderAttachments();
	const deleteOrderAttachment = useDeleteOrderAttachment();
	const [removedOrderAttachments, setRemovedOrderAttachments] = useState<OrderAttachment[]>([]);
	const isRemovedOrderAttachment = (item: OrderAttachment) =>
		removedOrderAttachments.some((r) =>
			r.source === "sap" && item.source === "sap"
				? r.line === item.line
				: r.source === "pending" && item.source === "pending"
					? r.index === item.index
					: false,
		);
	const [orderWarehouses, setOrderWarehouses] = useState<
		{ id: string; code: string; name: string }[]
	>([]);
	const [pendingFiles, setPendingFiles] = useState<File[]>([]);
	const existingAttachments = usePendingOfferAttachments(isEdit ? (editId ?? null) : null);
	const uploadAttachments = useUploadPendingOfferAttachments();
	const deleteAttachment = useDeletePendingOfferAttachment();
	const [removedAttachments, setRemovedAttachments] = useState<PendingOfferAttachment[]>([]);
	const isRemovedAttachment = (item: PendingOfferAttachment) =>
		removedAttachments.some((r) => r.index === item.index);
	const [prefilled, setPrefilled] = useState(false);
	// Kopyalanan teklifin formda görünmeyen alanları; yeni kayda olduğu gibi taşınır.
	const [copyExtras, setCopyExtras] = useState<{
		internalNotes?: string | undefined;
		opportunityId?: string | undefined;
	}>({});
	const { canDelegate, users: delegatableUsers } = useDelegation();
	const [onBehalfOf, setOnBehalfOf] = useState("");
	const search = initialSearch ?? {};
	const [header, setHeader] = useState({
		validUntil: DateTime.now().plus({ days: 10 }).toISODate() ?? "",
		currency: "TL",
		paymentTerms: "",
		deliveryTerms: "",
		notes: "",
		offerDate: DateTime.now().toISODate() ?? "",
		commitmentDate: "",
		commitmentValue: "",
		commitmentUnit: "day",
		offerOrderDeliveryType: "",
		isTaxIncluded: false,
		showGtipInPdf: false,
		pdfNotes: "",
		buyerExtraInfo: "",
		contractNo: "",
		contractDate: "",
		companyId: search.companyId ?? "",
		companySapAccountId: "",
		leadId: "",
		contactPersonId: search.contactPersonId ?? "",
	});
	const [termsLang, setTermsLang] = useState<"tr" | "en">(i18n.language === "en" ? "en" : "tr");
	const [termsI18n, setTermsI18n] = useState<Record<TermKey, { tr: string; en: string }>>({
		commissioning: { tr: "", en: "" },
		assembly: { tr: "", en: "" },
		vat: { tr: "", en: "" },
		quality: { tr: "", en: "" },
		validity: { tr: "", en: "" },
		warranty: { tr: "", en: "" },
		payment: { tr: "", en: "" },
		delivery: { tr: "", en: "" },
		paymentMethod: { tr: "", en: "" },
	});
	const setTermValue = (key: TermKey, value: string) => {
		setTermsI18n((prev) => ({ ...prev, [key]: { ...prev[key], [termsLang]: value } }));
	};
	const [initialOptions, setInitialOptions] = useState<
		Record<string, SearchableSelectOption | undefined>
	>({});
	const [formError, setFormError] = useState("");
	// İhraç grubundaki muhatap/potansiyel müşteride fiyatlar yurt dışı listesinden gelir.
	// "unknown": SAP grup listesi alınamadı, hangi listeden fiyat verileceği belirsiz.
	// Kullanıcı onaylayana kadar fiyat çekilmez.
	const [priceListType, setPriceListType] = useState<"domestic" | "export" | "unknown">("domestic");
	const [priceListBasis, setPriceListBasis] = useState<{
		reason: "country" | "group" | "default";
		country: string | null;
	} | null>(null);
	const [personModalOpen, setPersonModalOpen] = useState(false);
	const [newPerson, setNewPerson] = useState({ firstName: "", lastName: "", phone: "", email: "" });
	const [personError, setPersonError] = useState("");
	const [personSaving, setPersonSaving] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [selectedModelKey, setSelectedModelKey] = useState<Record<number, string>>({});
	const [modelInitialOptions, setModelInitialOptions] = useState<
		Record<number, SearchableSelectOption>
	>({});
	const [selectedSapKey, setSelectedSapKey] = useState<Record<number, string>>({});
	const [sapItemInitialOptions, setSapItemInitialOptions] = useState<
		Record<number, SearchableSelectOption>
	>({});
	// Seçilen modelin alternatör/kabin fiyatları (ürün adından bilgi türetmek için)
	const [linePrices, setLinePrices] = useState<
		Record<
			number,
			{
				alternators: Record<string, number>;
				cabins: Record<string, number>;
				currency: string | null;
				basePrice: number | null;
			}
		>
	>({});
	const [lineInfo, setLineInfo] = useState<
		Record<number, { label: string; price: number; currency: string | null }[]>
	>({});
	const [appliedInfo, setAppliedInfo] = useState<Record<number, Record<string, boolean>>>({});
	const [appliedAmount, setAppliedAmount] = useState<Record<number, Record<string, number>>>({});
	const [groupRateDate, setGroupRateDate] = useState<Record<number, string>>({});
	const [groupRates, setGroupRates] = useState<Record<number, Record<string, number>>>({});
	const { vatGroups, options: vatGroupOptions } = useVatGroups();
	const [lineWarning, setLineWarning] = useState<Record<number, string | null>>({});
	const [linePanels, setLinePanels] = useState<
		Record<number, Record<string, Record<string, number>>>
	>({});
	const [panelChecked, setPanelChecked] = useState<Record<number, Record<string, boolean>>>({});
	const [panelSelected, setPanelSelected] = useState<Record<number, Record<string, string>>>({});
	const [panelQty, setPanelQty] = useState<Record<number, Record<string, string>>>({});
	const [panelShow, setPanelShow] = useState<Record<number, Record<string, boolean>>>({});
	const [panelFolded, setPanelFolded] = useState<Record<number, Record<string, number>>>({});
	const [panelPriced, setPanelPriced] = useState<Record<number, Record<string, boolean>>>({});
	// "ATS Yok" / "CB Yok": anahtar "ATS" (ATS + Change Over ATS) veya "CB".
	// "Ayrı Faturala" işaretli pano grupları: fiyat ana kaleme katlanmaz, seçilen SAP
	// kalemiyle ana kalemin hemen altında ayrı satır olarak gönderilir.
	const [panelSeparate, setPanelSeparate] = useState<Record<number, Record<string, SeparatePanel>>>(
		{},
	);
	const [lineSource, setLineSource] = useState<Record<number, "priceList" | "sap">>({});
	// Satır portatif listesinden mi besleniyor? Aynı model hem ana listede (fiyatsız
	// PORTATİF sayfası) hem portatif listesinde bulunabildiği için model arama ve fiyat
	// sorgusu bu işarete göre tek kategoriye kilitlenir.
	const [linePortable, setLinePortable] = useState<Record<number, boolean>>({});
	const [directSapKey, setDirectSapKey] = useState<Record<number, string>>({});

	useEffect(() => {
		if (isEdit || copyFromId) return;
		api
			.get<{ key: string; value: string | null; valueI18n: Record<string, string> | null }[]>(
				"/api/settings/offer-terms",
			)
			.then((rows) => {
				const map: Record<string, { tr: string; en: string }> = {};
				for (const row of rows) {
					if (row.key.startsWith("terms_")) {
						map[row.key] = {
							tr: row.valueI18n?.["tr"] ?? row.value ?? "",
							en: row.valueI18n?.["en"] ?? "",
						};
					}
				}
				setTermsI18n((prev) => ({
					commissioning: map["terms_startup"] ?? prev.commissioning,
					assembly: map["terms_installation"] ?? prev.assembly,
					vat: map["terms_vat"] ?? prev.vat,
					quality: map["terms_quality_standard"] ?? prev.quality,
					validity: map["terms_validity"] ?? prev.validity,
					warranty: map["terms_warranty"] ?? prev.warranty,
					payment: map["terms_payment"] ?? prev.payment,
					delivery: map["terms_delivery"] ?? prev.delivery,
					paymentMethod: map["terms_payment_method"] ?? prev.paymentMethod,
				}));
			})
			.catch(() => {
				// leave empty defaults on error
			});
	}, []);

	useEffect(() => {
		const opts: Record<string, SearchableSelectOption> = {};
		const fetches: Promise<void>[] = [];
		if (header.companyId) {
			const selectValue = header.companySapAccountId || header.companyId;
			fetches.push(
				api
					.get<{ id: string; legalName: string }>(`/api/companies/${header.companyId}`)
					.then((c) => {
						opts.company = {
							value: selectValue,
							label: `${c.legalName}${header.currency ? ` (${header.currency})` : ""}`,
						};
					})
					.catch(() => {}),
			);
		}
		if (header.contactPersonId) {
			fetches.push(
				api
					.get<{ id: string; firstName: string; lastName: string }>(
						`/api/persons/${header.contactPersonId}`,
					)
					.then((p) => {
						opts.person = { value: p.id, label: `${p.firstName} ${p.lastName}` };
					})
					.catch(() => {}),
			);
		}

		if (fetches.length === 0) return;
		Promise.all(fetches).then(() => setInitialOptions((prev) => ({ ...prev, ...opts })));
	}, [header.companyId, header.companySapAccountId, header.currency, header.contactPersonId]);

	useEffect(() => {
		const companyId = header.companyId;
		if (!companyId || header.contactPersonId) return;
		let cancelled = false;
		api
			// API bu uçta birincil kişiyi başa alıyor, ama sıralama yalnızca çekilen sayfa içinde
			// yapılıyor: limit=1 ile en yeni kişi gelip birincil kaçabiliyordu.
			.get<{ items: { id: string; firstName: string; lastName: string; isPrimary: boolean }[] }>(
				`/api/persons?limit=50&companyId=${companyId}`,
			)
			.then((res) => {
				const primary = res.items.find((p) => p.isPrimary) ?? res.items[0];
				if (cancelled || !primary) return;
				setHeader((prev) =>
					prev.companyId === companyId && !prev.contactPersonId
						? { ...prev, contactPersonId: primary.id }
						: prev,
				);
				setInitialOptions((prev) => ({
					...prev,
					person: { value: primary.id, label: `${primary.firstName} ${primary.lastName}` },
				}));
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [header.companyId, header.contactPersonId]);
	const [lines, setLines] = useState<LineItem[]>([
		{
			lineNumber: 1,
			itemCode: "",
			itemDescription: "",
			displayDescription: "",
			quantity: "1",
			unit: "",
			unitPrice: "",
			givenUnitPrice: "",
			vatRate: "20",
			vatGroup: "",
			discountPercent: "0",
			notes: "",
			factorySpecialNotes: "",
			generalNotes: "",
			stockItemId: "",
			showInPdf: true,
			showPriceInPdf: true,
			showDiscountInPdf: false,
			sendDatasheetInEmail: true,
			isCustomDesign: false,
			lineDetail: "",
			priceListModel: "",
			commitmentValue: "",
			commitmentUnit: "day",
			alternativeGroup: 0,
			isSection: false,
			sectionItems: [],
			...ORDER_LINE_DEFAULTS,
		},
	]);

	const stockAvailability = useLineStockAvailability(
		isOrder ? [] : lines.filter((l) => !l.isSection).map((l) => l.stockItemId),
		editId,
	);
	const availabilityByItem = new Map(
		(stockAvailability.data?.lines ?? []).map((a) => [a.lineStockItemId, a]),
	);

	interface OfferDraft {
		header: typeof header;
		termsI18n: typeof termsI18n;
		lines: typeof lines;
	}
	const draft = useFormDraft<OfferDraft>("offer-draft", !isEdit && !copyFromId);

	const restoreDraft = () => {
		if (!draft.pendingDraft) return;
		setHeader(draft.pendingDraft.header);
		setTermsI18n(draft.pendingDraft.termsI18n);
		setLines(draft.pendingDraft.lines);
		const modelKeys: Record<number, string> = {};
		const modelOpts: Record<number, SearchableSelectOption> = {};
		const sapKeys: Record<number, string> = {};
		const sapOpts: Record<number, SearchableSelectOption> = {};
		draft.pendingDraft.lines.forEach((l, i) => {
			if (l.priceListModel) {
				modelKeys[i] = l.priceListModel;
				modelOpts[i] = { value: l.priceListModel, label: l.priceListModel };
			}
			if (l.stockItemId) {
				sapKeys[i] = l.stockItemId;
				sapOpts[i] = {
					value: l.stockItemId,
					label: `${l.itemCode ?? ""} — ${l.itemDescription ?? ""}`,
				};
			}
		});
		setSelectedModelKey(modelKeys);
		setModelInitialOptions(modelOpts);
		setSelectedSapKey(sapKeys);
		setSapItemInitialOptions(sapOpts);
		draft.acceptDraft();
	};

	useEffect(() => {
		if (draft.showPrompt || copyFromId) return;
		const hasData = !!(
			header.companyId ||
			header.contactPersonId ||
			header.notes ||
			header.pdfNotes ||
			header.buyerExtraInfo ||
			lines.some((l) => l.itemCode || l.itemDescription || l.stockItemId || Number(l.unitPrice) > 0)
		);
		if (hasData) draft.save({ header, termsI18n, lines });
		else draft.clear();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [header, termsI18n, lines, draft.showPrompt]);

	useEffect(() => {
		if ((!isEdit && !copyFromId) || prefilled || !detail.data) return;
		const o = detail.data;
		const d = (v: string | null) => (v ? v.slice(0, 10) : "");
		const isCopy = !isEdit;
		if (isCopy) {
			setCopyExtras({
				internalNotes: o.internalNotes ?? undefined,
				opportunityId: o.opportunityId ?? undefined,
			});
		}
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setHeader((prev) => ({
			...prev,
			validUntil: d(o.validUntil),
			currency: o.currency ?? prev.currency,
			paymentTerms: o.paymentTerms ?? "",
			deliveryTerms: o.deliveryTerms ?? "",
			notes: o.notes ?? "",
			offerDate: isCopy ? prev.offerDate : d(o.offerDate),
			commitmentDate: d(o.commitmentDate),
			commitmentValue: o.commitmentValue ?? "",
			commitmentUnit: o.commitmentUnit ?? "day",
			isTaxIncluded: o.isTaxIncluded ?? false,
			showGtipInPdf: o.showGtipInPdf ?? false,
			pdfNotes: o.pdfNotes ?? "",
			buyerExtraInfo: o.buyerExtraInfo ?? "",
			contractNo: o.contractNo ?? "",
			contractDate: d(o.contractDate),
			offerOrderDeliveryType: o.offerOrderDeliveryType ?? "",
			companyId: isCopy ? "" : (o.companyId ?? ""),
			companySapAccountId: isCopy ? "" : (o.companySapAccountId ?? ""),
			leadId: isCopy ? "" : (o.leadId ?? ""),
			contactPersonId: isCopy ? "" : (o.contactPersonId ?? ""),
		}));
		if (!isCopy) setOnBehalfOf(o.assignedTo ?? "");
		const legacyTerms: Record<TermKey, string | null> = {
			commissioning: o.termsCommissioning,
			assembly: o.termsAssembly,
			vat: o.termsVat,
			quality: o.termsQuality,
			validity: o.termsValidity,
			warranty: o.termsWarranty,
			payment: o.termsPayment,
			delivery: o.termsDelivery,
			paymentMethod: o.termsPaymentMethod,
		};
		setTermsI18n((prev) => {
			const next = { ...prev };
			for (const key of TERM_KEYS) {
				const stored = o.termsI18n?.[key];
				next[key] = {
					tr: stored?.["tr"] ?? legacyTerms[key] ?? prev[key].tr ?? "",
					en: stored?.["en"] ?? prev[key].en ?? "",
				};
			}
			return next;
		});
		// Pano (ATS / Change Over ATS / CB) satırları ana kalemin içine gömülü seçimlerdir;
		// düzenlemede ayrı kalem olarak açılmamalı. Bunları ayıklayıp ana kalemin pano
		// seçimlerine geri çeviriyoruz.
		const panelShownByParent: Record<number, Record<string, boolean>> = {};
		const panelPricedByParent: Record<number, Record<string, boolean>> = {};
		const separateByParent: Record<number, Record<string, SeparatePanelLoad>> = {};
		const sorted: typeof o.lines = [];
		for (const line of [...(o.lines ?? [])].sort((a, b) => a.lineNumber - b.lineNumber)) {
			const separate = sorted.length > 0 ? parseSeparatePanelLine(line) : null;
			if (separate) {
				const parentIdx = sorted.length - 1;
				separateByParent[parentIdx] = {
					...separateByParent[parentIdx],
					[separate.group]: separate,
				};
				continue;
			}
			const parsed =
				!line.itemCode && !line.stockItemId && !line.isSection
					? parsePanelLabel(line.displayDescription || line.itemDescription)
					: null;
			if (parsed && sorted.length > 0 && parsed.option === PANEL_NONE_OPTION) continue;
			if (parsed && sorted.length > 0) {
				const parentIdx = sorted.length - 1;
				panelShownByParent[parentIdx] = {
					...panelShownByParent[parentIdx],
					[parsed.group]: true,
				};
				panelPricedByParent[parentIdx] = {
					...panelPricedByParent[parentIdx],
					[parsed.group]: line.showPriceInPdf ?? false,
				};
				continue;
			}
			sorted.push(line);
		}

		// Ana kalemlerin priceComponents kaydından pano seçimlerini ve fiyata eklenmiş
		// alternatör/kabin rozetlerini geri yükle. Tutarlar zaten birim fiyata katlanmış
		// durumda; panelFolded/appliedAmount'a yazıyoruz ki tekrar eklenmesin.
		const pChecked: Record<number, Record<string, boolean>> = {};
		const pSelected: Record<number, Record<string, string>> = {};
		const pQty: Record<number, Record<string, string>> = {};
		const pShow: Record<number, Record<string, boolean>> = {};
		const pFolded: Record<number, Record<string, number>> = {};
		const infoApplied: Record<number, Record<string, boolean>> = {};
		const infoAmounts: Record<number, Record<string, number>> = {};
		sorted.forEach((l, i) => {
			for (const component of l.priceComponents ?? []) {
				const parsed = parsePanelLabel(component.label);
				if (parsed) {
					pChecked[i] = { ...pChecked[i], [parsed.group]: true };
					pSelected[i] = { ...pSelected[i], [parsed.group]: parsed.option };
					pQty[i] = { ...pQty[i], [parsed.group]: String(parsed.qty) };
					pShow[i] = {
						...pShow[i],
						[parsed.group]: panelShownByParent[i]?.[parsed.group] ?? false,
					};
					pFolded[i] = { ...pFolded[i], [parsed.group]: component.price };
				} else {
					infoApplied[i] = { ...infoApplied[i], [component.label]: true };
					infoAmounts[i] = { ...infoAmounts[i], [component.label]: component.price };
				}
			}
			for (const [group, s] of Object.entries(separateByParent[i] ?? {})) {
				pChecked[i] = { ...pChecked[i], [group]: true };
				pSelected[i] = { ...pSelected[i], [group]: s.option };
				pQty[i] = { ...pQty[i], [group]: String(s.qty) };
				pFolded[i] = { ...pFolded[i], [group]: 0 };
			}
		});
		setPanelChecked(pChecked);
		setPanelSelected(pSelected);
		setPanelQty(pQty);
		setPanelShow(pShow);
		setPanelSeparate(separatePanelState(separateByParent));
		setPanelPriced(panelPricedByParent);
		setPanelFolded(pFolded);
		setAppliedInfo(infoApplied);
		setAppliedAmount(infoAmounts);

		const modelKeys: Record<number, string> = {};
		const modelOpts: Record<number, SearchableSelectOption> = {};
		const sapKeys: Record<number, string> = {};
		const sapOpts: Record<number, SearchableSelectOption> = {};
		sorted.forEach((l, i) => {
			if (l.priceListModel) {
				modelKeys[i] = l.priceListModel;
				modelOpts[i] = { value: l.priceListModel, label: l.priceListModel };
			}
			if (l.stockItemId) {
				sapKeys[i] = l.stockItemId;
				sapOpts[i] = {
					value: l.stockItemId,
					label: `${l.itemCode ?? ""} — ${l.itemDescription ?? ""}`,
				};
			}
		});
		setSelectedModelKey(modelKeys);
		setModelInitialOptions(modelOpts);
		setSelectedSapKey(sapKeys);
		setSapItemInitialOptions(sapOpts);
		if (sorted.length > 0) {
			setLines(
				sorted.map((l, i) => ({
					lineNumber: i + 1,
					itemCode: l.itemCode ?? "",
					itemDescription: l.itemDescription ?? "",
					displayDescription: l.displayDescription ?? "",
					quantity: l.quantity ?? "1",
					unit: l.unit ?? "",
					unitPrice: l.unitPrice ?? "",
					givenUnitPrice: l.givenUnitPrice ?? "",
					vatRate: l.vatRate ?? "20",
					vatGroup: l.vatGroup ?? "",
					discountPercent: l.discountPercent != null ? String(Number(l.discountPercent)) : "0",
					notes: l.notes ?? "",
					factorySpecialNotes: l.factorySpecialNotes ?? "",
					generalNotes: l.generalNotes ?? "",
					stockItemId: l.stockItemId ?? "",
					showInPdf: l.showInPdf ?? true,
					showPriceInPdf: l.showPriceInPdf ?? true,
					showDiscountInPdf: l.showDiscountInPdf ?? false,
					sendDatasheetInEmail: l.sendDatasheetInEmail ?? true,
					isCustomDesign: l.isCustomDesign ?? false,
					lineDetail: l.lineDetail ?? "",
					priceListModel: l.priceListModel ?? "",
					commitmentValue: l.commitmentValue ?? "",
					commitmentUnit: l.commitmentUnit ?? "day",
					alternativeGroup: l.alternativeGroup ?? 0,
					isSection: l.isSection ?? false,
					sectionItems:
						l.isSection && (l.sectionItems ?? []).length === 0
							? [{ name: "", quantity: "1", unitPrice: String(l.unitPrice ?? 0) }]
							: (l.sectionItems ?? []).map((it) => ({
									name: it.name,
									quantity: String(it.quantity),
									unitPrice: String(it.unitPrice),
								})),
					...ORDER_LINE_DEFAULTS,
				})),
			);
		}
		const groupCur: Record<number, string> = {};
		sorted.forEach((l) => {
			const g = l.alternativeGroup ?? 0;
			if (l.alternativeCurrency && groupCur[g] === undefined) groupCur[g] = l.alternativeCurrency;
		});
		if (Object.keys(groupCur).length > 0) setGroupCurrency((prev) => ({ ...groupCur, ...prev }));

		setPrefilled(true);
	}, [isEdit, copyFromId, prefilled, detail.data]);

	useEffect(() => {
		if (!isOrder || prefilled || !orderDetail.data) return;
		const o = orderDetail.data;
		const d = (v: string | null) => (v ? v.slice(0, 10) : "");
		if (isOrderCopy) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setCopyExtras({ internalNotes: o.internalNotes ?? undefined });
		}
		setHeader((prev) => ({
			...prev,
			currency: o.currency ?? prev.currency,
			paymentTerms: o.paymentTerms ?? "",
			deliveryTerms: o.deliveryTerms ?? "",
			notes: o.notes ?? "",
			companyId: isOrderCopy ? "" : (o.companyId ?? ""),
			contactPersonId: isOrderCopy ? "" : (o.contactPersonId ?? ""),
		}));
		setOrderHeader({
			sourceOfferId: isOrderCopy ? "" : (o.sourceOfferId ?? ""),
			status: isOrderCopy ? "confirmed" : (o.status ?? "confirmed"),
			orderDate: isOrderCopy ? (DateTime.now().toISODate() ?? "") : d(o.orderDate),
			deliveryDate: d(o.deliveryDate),
			shippingAddress: isOrderCopy ? "" : (o.shippingAddress ?? ""),
			shippingAddressId: isOrderCopy ? "" : (o.shippingAddressId ?? ""),
			startupDescription: o.startupDescription ?? "",
			installationDescription: o.installationDescription ?? "",
		});
		if (o.currency) setGroupCurrency({ 0: o.currency });

		// Pano satırları kaydederken pano seçimlerinden yeniden üretilir; diğer kodsuz
		// satırlar (teklifteki başlıklar) düzenlenebilir başlık kartı olarak açılır.
		const sorted: OrderDetailLine[] = [];
		const panelLinesByParent: Record<number, Record<string, { option: string; qty: number }>> = {};
		const separateByParent: Record<number, Record<string, SeparatePanelLoad>> = {};
		for (const line of [...o.lines].sort((a, b) => a.lineNumber - b.lineNumber)) {
			const separate = sorted.length > 0 ? parseSeparatePanelLine(line) : null;
			if (separate) {
				const parentIdx = sorted.length - 1;
				separateByParent[parentIdx] = {
					...separateByParent[parentIdx],
					[separate.group]: separate,
				};
				continue;
			}
			const parsed =
				!line.itemCode && !line.stockItemId
					? parsePanelLabel(line.displayDescription || line.itemDescription)
					: null;
			if (parsed && sorted.length > 0 && parsed.option === PANEL_NONE_OPTION) continue;
			if (parsed && sorted.length > 0) {
				const parentIdx = sorted.length - 1;
				panelLinesByParent[parentIdx] = {
					...panelLinesByParent[parentIdx],
					[parsed.group]: { option: parsed.option, qty: Number(line.quantity) || parsed.qty },
				};
				continue;
			}
			sorted.push(line);
		}

		const pChecked: Record<number, Record<string, boolean>> = {};
		const pSelected: Record<number, Record<string, string>> = {};
		const pQty: Record<number, Record<string, string>> = {};
		const pShow: Record<number, Record<string, boolean>> = {};
		const pFolded: Record<number, Record<string, number>> = {};
		const infoApplied: Record<number, Record<string, boolean>> = {};
		const infoAmounts: Record<number, Record<string, number>> = {};
		const setPanel = (i: number, group: string, option: string, qty: number, folded: number) => {
			pChecked[i] = { ...pChecked[i], [group]: true };
			pSelected[i] = { ...pSelected[i], [group]: option };
			pQty[i] = { ...pQty[i], [group]: String(qty) };
			pShow[i] = { ...pShow[i], [group]: true };
			pFolded[i] = { ...pFolded[i], [group]: folded };
		};
		sorted.forEach((l, i) => {
			for (const component of l.priceComponents ?? []) {
				const parsed = parsePanelLabel(component.label);
				if (parsed) {
					setPanel(i, parsed.group, parsed.option, parsed.qty, component.price);
				} else {
					infoApplied[i] = { ...infoApplied[i], [component.label]: true };
					infoAmounts[i] = { ...infoAmounts[i], [component.label]: component.price };
				}
			}
			for (const [group, panel] of Object.entries(panelLinesByParent[i] ?? {})) {
				if (!pChecked[i]?.[group]) setPanel(i, group, panel.option, panel.qty, 0);
			}
			for (const [group, s] of Object.entries(separateByParent[i] ?? {})) {
				setPanel(i, group, s.option, s.qty, 0);
			}
		});
		setPanelChecked(pChecked);
		setPanelSelected(pSelected);
		setPanelQty(pQty);
		setPanelShow(pShow);
		setPanelSeparate(separatePanelState(separateByParent));
		setPanelFolded(pFolded);
		setAppliedInfo(infoApplied);
		setAppliedAmount(infoAmounts);

		const modelKeys: Record<number, string> = {};
		const modelOpts: Record<number, SearchableSelectOption> = {};
		const sapKeys: Record<number, string> = {};
		const sapOpts: Record<number, SearchableSelectOption> = {};
		const sources: Record<number, "priceList" | "sap"> = {};
		sorted.forEach((l, i) => {
			if (l.priceListModel) {
				modelKeys[i] = l.priceListModel;
				modelOpts[i] = { value: l.priceListModel, label: l.priceListModel };
			} else {
				sources[i] = "sap";
			}
			if (l.stockItemId) {
				sapKeys[i] = l.stockItemId;
				sapOpts[i] = {
					value: l.stockItemId,
					label: `${l.itemCode ?? ""} — ${l.itemDescription ?? ""}`,
				};
			}
		});
		setSelectedModelKey(modelKeys);
		setModelInitialOptions(modelOpts);
		setSelectedSapKey(sapKeys);
		setSapItemInitialOptions(sapOpts);
		setDirectSapKey(
			Object.fromEntries(Object.keys(sources).map((i) => [i, sapKeys[Number(i)] ?? ""])),
		);
		setLineSource(sources);
		if (sorted.length > 0) {
			setLines(
				sorted.map((l, i) => ({
					lineNumber: i + 1,
					itemCode: l.itemCode ?? "",
					itemDescription: l.itemDescription ?? "",
					displayDescription: l.displayDescription ?? "",
					quantity: l.quantity ?? "1",
					unit: l.unit ?? "",
					unitPrice: l.unitPrice ?? "",
					givenUnitPrice: "",
					vatRate: l.vatRate ?? "20",
					vatGroup: l.vatGroup ?? "",
					discountPercent: l.discountPercent != null ? String(Number(l.discountPercent)) : "0",
					notes: l.notes ?? "",
					factorySpecialNotes: l.factorySpecialNotes ?? "",
					generalNotes: l.generalNotes ?? "",
					stockItemId: l.stockItemId ?? "",
					showInPdf: true,
					showPriceInPdf: true,
					showDiscountInPdf: false,
					sendDatasheetInEmail: false,
					isCustomDesign: l.isCustomDesign ?? false,
					lineDetail: l.lineDetail ?? "",
					priceListModel: l.priceListModel ?? "",
					commitmentValue: "",
					commitmentUnit: "day",
					alternativeGroup: 0,
					isSection: !l.itemCode && !l.stockItemId,
					sectionItems:
						!l.itemCode && !l.stockItemId
							? [{ name: "", quantity: "1", unitPrice: String(Number(l.unitPrice) || 0) }]
							: [],
					warehouseId: l.warehouseId ?? "",
					atsDescription: l.atsDescription ?? "",
					tmsDescription: l.tmsDescription ?? "",
				})),
			);
		}
		setPrefilled(true);
	}, [isOrder, isOrderCopy, prefilled, orderDetail.data]);

	useEffect(() => {
		if (!isOrder) return;
		api
			.get<{ items: { id: string; code: string; name: string; isActive: boolean }[] }>(
				"/api/warehouses?limit=200",
			)
			.then((res) =>
				setOrderWarehouses(
					(res.items ?? [])
						.filter((w) => w.isActive)
						.map((w) => ({ id: w.id, code: w.code, name: w.name })),
				),
			)
			.catch(() => setOrderWarehouses([]));
	}, [isOrder]);

	const assignDefaultWarehouse = async (idx: number, stockItemId: string) => {
		if (!isOrder || !stockItemId) return;
		try {
			const res = await api.get<{
				warehouseStocks: { warehouseId: string; currentStock: string | null }[];
				defaultWarehouseCode: string | null;
			}>(`/api/stock-items/${stockItemId}`);
			const byDefault = res.defaultWarehouseCode
				? orderWarehouses.find((w) => w.code === res.defaultWarehouseCode)?.id
				: undefined;
			const byStock = [...(res.warehouseStocks ?? [])].sort(
				(a, b) => Number(b.currentStock) - Number(a.currentStock),
			)[0]?.warehouseId;
			const warehouseId = byDefault ?? byStock;
			if (!warehouseId) return;
			setLines((prev) => {
				const line = prev[idx];
				if (!line || line.stockItemId !== stockItemId) return prev;
				const next = [...prev];
				next[idx] = { ...line, warehouseId };
				return next;
			});
		} catch {
			// Depo bulunamazsa kullanıcı elle seçer.
		}
	};

	const addLine = (group = 0) => {
		setLines([
			...lines,
			{
				lineNumber: lines.length + 1,
				itemCode: "",
				itemDescription: "",
				displayDescription: "",
				quantity: "1",
				unit: "",
				unitPrice: "",
				givenUnitPrice: "",
				vatRate: "20",
				vatGroup: "",
				discountPercent: "0",
				notes: "",
				factorySpecialNotes: "",
				generalNotes: "",
				stockItemId: "",
				showInPdf: true,
				showPriceInPdf: true,
				showDiscountInPdf: false,
				sendDatasheetInEmail: true,
				isCustomDesign: false,
				lineDetail: "",
				priceListModel: "",
				commitmentValue: "",
				commitmentUnit: "day",
				alternativeGroup: group,
				isSection: false,
				sectionItems: [],
				...ORDER_LINE_DEFAULTS,
			},
		]);
	};

	/** Ürünlerin altına başlıklı bir kalem grubu ekler (fiyatı PDF'te varsayılan gizli). */
	const addSection = (group = 0) => {
		setLines((prev) => [
			...prev,
			{
				lineNumber: prev.length + 1,
				itemCode: "",
				itemDescription: "",
				displayDescription: "",
				quantity: "1",
				unit: "Adet",
				unitPrice: "0",
				givenUnitPrice: "",
				vatRate: "20",
				vatGroup: "",
				discountPercent: "0",
				notes: "",
				factorySpecialNotes: "",
				generalNotes: "",
				stockItemId: "",
				showInPdf: true,
				showPriceInPdf: false,
				showDiscountInPdf: false,
				sendDatasheetInEmail: false,
				isCustomDesign: false,
				lineDetail: "",
				priceListModel: "",
				commitmentValue: "",
				commitmentUnit: "day",
				alternativeGroup: group,
				isSection: true,
				sectionItems: [{ name: "", quantity: "1", unitPrice: "" }],
				...ORDER_LINE_DEFAULTS,
			},
		]);
	};

	const setSectionTitle = (idx: number, title: string) => {
		setLines((prev) => {
			const line = prev[idx];
			if (!line) return prev;
			const next = [...prev];
			next[idx] = { ...line, itemDescription: title, displayDescription: title };
			return next;
		});
	};

	const updateSectionItems = (idx: number, items: SectionItem[]) => {
		setLines((prev) => {
			const line = prev[idx];
			if (!line) return prev;
			const next = [...prev];
			next[idx] = {
				...line,
				sectionItems: items,
				unitPrice: String(sectionUnitPrice(items)),
			};
			return next;
		});
	};

	const updateSectionItem = (
		idx: number,
		itemIdx: number,
		field: keyof SectionItem,
		value: string,
	) => {
		const items = (lines[idx]?.sectionItems ?? []).map((it, i) =>
			i === itemIdx ? { ...it, [field]: value } : it,
		);
		updateSectionItems(idx, items);
	};

	const addSectionItem = (idx: number) => {
		updateSectionItems(idx, [
			...(lines[idx]?.sectionItems ?? []),
			{ name: "", quantity: "1", unitPrice: "" },
		]);
	};

	const removeSectionItem = (idx: number, itemIdx: number) => {
		updateSectionItems(
			idx,
			(lines[idx]?.sectionItems ?? []).filter((_, i) => i !== itemIdx),
		);
	};

	const [groupCurrency, setGroupCurrency] = useState<Record<number, string>>({});
	const alternativeGroups = [...new Set(lines.map((l) => l.alternativeGroup))].sort(
		(a, b) => a - b,
	);
	const currencyOfGroup = (group: number) => groupCurrency[group] ?? header.currency;

	// Başlıkta ayrı bir para birimi alanı yok; teklifin para birimi ilk alternatifte
	// seçilen para birimidir (fiyat listesi header.currency'yi güncelleyebiliyor).
	const offerCurrency = groupCurrency[0] ?? header.currency;

	// Teklif zaten SAP'ye gönderildiyse (sapDocEntry dolu) ve kullanıcı muhatabı
	// değiştirdiyse: SAP'de CardCode değiştirilemediği için bu değişiklik senkronda
	// sessizce atlanır; kullanıcıyı bilgilendirip yeni teklif oluşturmaya yönlendiriyoruz.
	const sapAccountChangeWarning =
		isEdit &&
		!!detail.data?.sapDocEntry &&
		(header.companyId !== (detail.data?.companyId ?? "") ||
			header.companySapAccountId !== (detail.data?.companySapAccountId ?? ""))
			? t("sapCompanyChangeWarning")
			: "";

	const LOCAL_CURRENCIES = new Set(["TL", "TRY"]);
	// 1 birim dövizin yerel para karşılığı; yerel para için 1
	const rateFor = (group: number, currency: string | null | undefined): number | null => {
		if (!currency) return null;
		if (LOCAL_CURRENCIES.has(currency)) return 1;
		const r = groupRates[group]?.[currency];
		return r && r > 0 ? r : null;
	};
	const convertPrice = (
		price: number,
		fromCurrency: string | null | undefined,
		group: number,
	): { value: number; ok: boolean; converted: boolean } => {
		const target = currencyOfGroup(group);
		if (!fromCurrency || fromCurrency === target)
			return { value: price, ok: true, converted: false };
		const rf = rateFor(group, fromCurrency);
		const rt = rateFor(group, target);
		if (rf == null || rt == null) return { value: price, ok: false, converted: false };
		const value = Math.round(((price * rf) / rt) * 100) / 100;
		return { value, ok: true, converted: true };
	};

	const fetchGroupRates = async (group: number, date: string) => {
		setGroupRateDate((prev) => ({ ...prev, [group]: date }));
		if (!date) return;
		try {
			const rows = await api.get<Array<{ currency: string; rate: number }>>(
				`/api/sap/currency-rates?date=${date}`,
			);
			const map: Record<string, number> = {};
			for (const r of rows) map[r.currency] = r.rate;
			setGroupRates((prev) => ({ ...prev, [group]: map }));
			recomputeGroupApplied(group, currencyOfGroup(group), map);
		} catch {
			setGroupRates((prev) => ({ ...prev, [group]: {} }));
		}
	};

	const recomputeGroupApplied = (
		group: number,
		target: string,
		ratesMap: Record<string, number>,
	) => {
		const rateOf = (currency: string | null | undefined): number | null => {
			if (!currency) return null;
			if (LOCAL_CURRENCIES.has(currency)) return 1;
			const r = ratesMap[currency];
			return r && r > 0 ? r : null;
		};
		const nextAmounts: Record<number, Record<string, number>> = { ...appliedAmount };
		setLines((prev) =>
			prev.map((line, idx) => {
				if (line.alternativeGroup !== group) return line;
				const appliedLabels = Object.keys(appliedInfo[idx] ?? {}).filter(
					(l) => appliedInfo[idx]?.[l],
				);
				if (appliedLabels.length === 0) return line;
				let base = Number(line.unitPrice) || 0;
				for (const label of appliedLabels) base -= appliedAmount[idx]?.[label] ?? 0;
				const newAmounts: Record<string, number> = {};
				for (const label of appliedLabels) {
					const info = lineInfo[idx]?.find((i) => i.label === label);
					const price = info?.price ?? 0;
					const from = info?.currency ?? null;
					let value = price;
					if (from && from !== target) {
						const rf = rateOf(from);
						const rt = rateOf(target);
						if (rf != null && rt != null) value = Math.round(((price * rf) / rt) * 100) / 100;
					}
					newAmounts[label] = value;
					base += value;
				}
				nextAmounts[idx] = newAmounts;
				const newList = Math.max(0, Math.round(base * 100) / 100);
				const given = line.givenUnitPrice;
				const disc =
					given && given.trim() !== ""
						? String(computeDiscount(newList, Number(given) || 0))
						: line.discountPercent;
				return { ...line, unitPrice: String(newList), discountPercent: disc } as LineItem;
			}),
		);
		setAppliedAmount(nextAmounts);
	};

	const changeGroupCurrency = (group: number, target: string) => {
		setGroupCurrency((prev) => ({ ...prev, [group]: target }));
		recomputeGroupApplied(group, target, groupRates[group] ?? {});
	};

	const addAlternative = () => {
		const nextGroup = (alternativeGroups[alternativeGroups.length - 1] ?? 0) + 1;
		setGroupCurrency((prev) => ({ ...prev, [nextGroup]: header.currency }));
		addLine(nextGroup);
	};

	const removeAlternative = (group: number) => {
		setLines((prev) =>
			prev.filter((l) => l.alternativeGroup !== group).map((l, i) => ({ ...l, lineNumber: i + 1 })),
		);
	};

	/** Ana kalemin ayrı faturalanan pano satırları (seçeneği seçilmiş olanlar). */
	const separatePanels = (idx: number) =>
		["ATS", "Change Over ATS", "CB"].flatMap((group) => {
			const panel = panelSeparate[idx]?.[group];
			const option = panelSelected[idx]?.[group] ?? "";
			const qty = Number(panelQty[idx]?.[group] ?? "1") || 0;
			if (!panel || !(panelChecked[idx]?.[group] ?? false) || !option || qty <= 0) return [];
			return [{ group, option, qty, panel }];
		});

	const findIncompleteSeparatePanel = () => {
		for (const [idx] of lines.entries()) {
			for (const group of ["ATS", "Change Over ATS", "CB"]) {
				const panel = panelSeparate[idx]?.[group];
				if (!panel || !(panelChecked[idx]?.[group] ?? false)) continue;
				if (!panelSelected[idx]?.[group] || !panel.stockItemId) return { idx, group };
			}
		}
		return null;
	};

	const separatePanelsTotal = (idx: number) =>
		separatePanels(idx).reduce((sum, s) => sum + s.qty * s.panel.unitPrice, 0);

	// Net birim fiyat: verilen fiyat girildiyse doğrudan o (yuvarlama sapması olmaz),
	// yoksa liste × (1 − iskonto).
	const netUnitPrice = (l: LineItem): number => {
		const given = l.givenUnitPrice?.trim();
		if (given) return Number(given) || 0;
		return (Number(l.unitPrice) || 0) * (1 - (Number(l.discountPercent) || 0) / 100);
	};

	// Ayrı faturalanan pano satırları ana kalemin KDV oranıyla gönderilir.
	const lineNetAmount = (l: LineItem, idx: number): number =>
		(Number(l.quantity) || 0) * netUnitPrice(l) + separatePanelsTotal(idx);

	const groupTotals = (group: number) => {
		const groupLines = lines
			.map((l, idx) => ({ l, idx }))
			.filter(({ l }) => l.alternativeGroup === group);
		const subtotal = groupLines.reduce((sum, { l, idx }) => sum + lineNetAmount(l, idx), 0);
		const vatTotal = groupLines.reduce(
			(sum, { l, idx }) => sum + lineNetAmount(l, idx) * ((Number(l.vatRate) || 0) / 100),
			0,
		);
		return { subtotal, vatTotal, total: subtotal + vatTotal };
	};

	const removeLine = (idx: number) => {
		setLines(lines.filter((_, i) => i !== idx).map((l, i) => ({ ...l, lineNumber: i + 1 })));
	};

	const updateLine = (idx: number, field: keyof LineItem, value: string) => {
		const next = [...lines];
		next[idx] = { ...next[idx], [field]: value } as LineItem;
		setLines(next);
	};

	const computeDiscount = (listPrice: number, given: number): number => {
		if (!(listPrice > 0)) return 0;
		return Math.round((1 - given / listPrice) * 10000) / 100;
	};

	const isManualListPrice = (idx: number): boolean =>
		(lineInfo[idx]?.length ?? 0) === 0 && Object.keys(linePanels[idx] ?? {}).length === 0;

	const setListPrice = (idx: number, value: string) => {
		setLines((prev) => {
			const line = prev[idx];
			if (!line) return prev;
			const next = [...prev];
			const given = line.givenUnitPrice?.trim();
			next[idx] = {
				...line,
				unitPrice: value,
				...(given
					? { discountPercent: String(computeDiscount(Number(value) || 0, Number(given) || 0)) }
					: {}),
			} as LineItem;
			return next;
		});
	};

	const setGivenPrice = (idx: number, value: string) => {
		setLines((prev) => {
			const line = prev[idx];
			if (!line) return prev;
			const list = Number(line.unitPrice) || 0;
			const next = [...prev];
			if (value.trim() === "") {
				next[idx] = { ...line, givenUnitPrice: "", discountPercent: "0" } as LineItem;
			} else {
				const disc = computeDiscount(list, Number(value) || 0);
				next[idx] = { ...line, givenUnitPrice: value, discountPercent: String(disc) } as LineItem;
			}
			return next;
		});
	};

	const setDiscountPercent = (idx: number, value: string) => {
		setLines((prev) => {
			const line = prev[idx];
			if (!line) return prev;
			const next = [...prev];
			next[idx] = {
				...line,
				discountPercent: value.trim() === "" ? "0" : value,
				givenUnitPrice: "",
			} as LineItem;
			return next;
		});
	};

	const syncDiscount = (idx: number) => {
		setLines((prev) => {
			const line = prev[idx];
			if (!line) return prev;
			const given = line.givenUnitPrice;
			if (!given || given.trim() === "") return prev;
			const disc = computeDiscount(Number(line.unitPrice) || 0, Number(given) || 0);
			const next = [...prev];
			next[idx] = { ...line, discountPercent: String(disc) } as LineItem;
			return next;
		});
	};

	const toggleLineFlag = (idx: number, flag: keyof LineItem) => {
		const next = [...lines];
		const line = next[idx]!;
		next[idx] = { ...line, [flag]: !line[flag] } as LineItem;
		setLines(next);
	};

	const calcLineTotal = (line: LineItem) => {
		const qty = Number(line.quantity) || 0;
		const vat = Number(line.vatRate) || 0;
		const base = qty * netUnitPrice(line);
		return base * (1 + vat / 100);
	};

	// Kartta gösterilen satır toplamı ayrı faturalanan panoları da içerir (KDV dahil);
	// kaydedilen lineTotal yalnızca ana kalemindir.
	const displayLineTotal = (line: LineItem, idx: number) =>
		calcLineTotal(line) + separatePanelsTotal(idx) * (1 + (Number(line.vatRate) || 0) / 100);

	const buildPriceComponents = (idx: number) => {
		const components: { label: string; price: number }[] = [];
		for (const info of lineInfo[idx] ?? []) {
			if (appliedInfo[idx]?.[info.label]) components.push({ label: info.label, price: info.price });
		}
		for (const group of ["ATS", "Change Over ATS", "CB"]) {
			if (!(panelChecked[idx]?.[group] ?? false)) continue;
			if (panelSeparate[idx]?.[group]) continue;
			const option = panelSelected[idx]?.[group] ?? "";
			if (!option) continue;
			const qty = Number(panelQty[idx]?.[group] ?? "1") || 0;
			const price = linePanels[idx]?.[group]?.[option] ?? 0;
			if (qty <= 0) continue;
			components.push({
				label: qty > 1 ? `${group} - ${option} x${qty}` : `${group} - ${option}`,
				price: price * qty,
			});
		}
		return components;
	};

	const buildPanelLines = (idx: number) => {
		const parent = lines[idx];
		if (!parent) return [];
		const groups = Object.keys(linePanels[idx] ?? {});
		const result: Array<{
			itemDescription: string;
			quantity: number;
			unit: string;
			unitPrice: number;
			vatRate: number;
			showPriceInPdf: boolean;
		}> = [];
		for (const group of ["ATS", "Change Over ATS", "CB"]) {
			if (!groups.includes(group)) continue;
			if (!(panelChecked[idx]?.[group] ?? false)) continue;
			if (panelSeparate[idx]?.[group]) continue;
			if (!(panelShow[idx]?.[group] ?? true)) continue;
			const option = panelSelected[idx]?.[group] ?? "";
			if (!option) continue;
			const qty = Number(panelQty[idx]?.[group] ?? "1") || 0;
			if (qty <= 0) continue;
			result.push({
				itemDescription: `${group} - ${option}`,
				quantity: qty,
				unit: "Adet",
				unitPrice: 0,
				vatRate: Number(parent.vatRate) || 0,
				showPriceInPdf: panelPriced[idx]?.[group] ?? false,
			});
		}
		return result;
	};

	// Ayrı faturalanan panolar: seçilen SAP kalemiyle fiyatlı satır. priceComponents'teki
	// `separate` işareti, düzenlemede satırın yeniden ana kalemin pano seçimine bağlanmasını sağlar.
	const buildSeparateLines = (idx: number) => {
		const parent = lines[idx];
		if (!parent) return [];
		return separatePanels(idx).map(({ group, option, qty, panel }) => {
			const label = qty > 1 ? `${group} - ${option} x${qty}` : `${group} - ${option}`;
			const vatRate = Number(parent.vatRate) || 0;
			return {
				itemCode: panel.itemCode || undefined,
				itemDescription: panel.itemDescription || undefined,
				displayDescription: panel.itemDescription || undefined,
				generalNotes: parent.itemCode ? `Bağlı Jeneratör: ${parent.itemCode}` : undefined,
				quantity: qty,
				unit: panel.unit || undefined,
				unitPrice: panel.unitPrice,
				vatRate,
				vatGroup: parent.vatGroup || undefined,
				discountPercent: 0,
				lineTotal: qty * panel.unitPrice * (1 + vatRate / 100),
				stockItemId: panel.stockItemId || undefined,
				isCustomDesign: parent.isCustomDesign,
				priceComponents: [{ label, price: qty * panel.unitPrice, separate: true }],
			};
		});
	};

	const subtotal = lines.reduce((sum, l, idx) => sum + lineNetAmount(l, idx), 0);
	const vatTotal = lines.reduce(
		(sum, l, idx) => sum + lineNetAmount(l, idx) * ((Number(l.vatRate) || 0) / 100),
		0,
	);
	const total = subtotal + vatTotal;

	const loadCompanies = useCallback(
		async (query: string) => {
			const res = await api.get<{
				items: Array<{
					id: string;
					legalName: string;
					currency: string | null;
					companySapAccountId: string | null;
				}>;
			}>(
				// Başkası adına teklif açılırken yalnızca seçilen kişinin carileri listelenir.
				`/api/companies?limit=50&status=customer${onBehalfOf ? `&salesPerson=${encodeURIComponent(onBehalfOf)}&includePublic=true` : ""}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			// Aynı firmanın birden fazla para birimi carisi olabilir (companySapAccountId ile
			// ayrışır); değer olarak firma id'sini kullanırsak üç satır da "aynı" görünüp hepsi
			// seçili gibi işaretlenir. Benzersiz değer için hesap id'sini, yoksa firma id'sini kullan.
			return res.items.map((c) => ({
				value: c.companySapAccountId ?? c.id,
				label: `${c.legalName}${c.currency ? ` (${c.currency})` : ""}`,
				meta: {
					companyId: c.id,
					companySapAccountId: c.companySapAccountId ?? "",
					currency: c.currency ?? "",
					legalName: c.legalName,
				},
			}));
		},
		[onBehalfOf],
	);

	// Potansiyel müşteri teklifleri SAP'a gitmez; aynı firma muhatap olarak kayıtlıysa onu öner.
	const [leadCompanyMatches, setLeadCompanyMatches] = useState<SearchableSelectOption[]>([]);
	useEffect(() => {
		setLeadCompanyMatches([]);
		if (isOrder || !header.leadId || header.companyId) return;
		let cancelled = false;
		void (async () => {
			try {
				const lead = await api.get<{ companyName: string | null }>(
					`/api/leads/${header.leadId}`,
				);
				const leadName = normalizeCompanyName(lead.companyName ?? "");
				if (!leadName) return;
				const options = await loadCompanies(lead.companyName ?? "");
				const matches = options.filter((o) => {
					const name = normalizeCompanyName(o.meta?.legalName ?? o.label);
					return !!name && (name === leadName || name.startsWith(`${leadName} `));
				});
				if (!cancelled) setLeadCompanyMatches(matches.slice(0, 5));
			} catch {
				if (!cancelled) setLeadCompanyMatches([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [isOrder, header.leadId, header.companyId, loadCompanies]);

	const selectSuggestedCompany = (option: SearchableSelectOption) => {
		setHeader((prev) => ({
			...prev,
			companyId: option.meta?.companyId ?? option.value,
			companySapAccountId: option.meta?.companySapAccountId || "",
			currency: option.meta?.currency || prev.currency,
			leadId: "",
			contactPersonId: "",
		}));
		setInitialOptions((prev) => ({ ...prev, company: option, lead: undefined, person: undefined }));
	};

	const loadOrderOffers = useCallback(
		async (query: string) => {
			if (!header.companyId) return [];
			const res = await api.get<{
				items: Array<{ id: string; documentNumber: string | null; companyName: string | null }>;
			}>(
				`/api/offers?limit=50&companyId=${header.companyId}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			return res.items.map((o) => ({
				value: o.id,
				label: o.companyName
					? `${o.companyName} — ${o.documentNumber || o.id.slice(0, 8)}`
					: o.documentNumber || o.id.slice(0, 8),
			}));
		},
		[header.companyId],
	);

	useEffect(() => {
		if (!isOrder || !orderHeader.sourceOfferId) return;
		let cancelled = false;
		api
			.get<{ id: string; documentNumber: string | null; companyName: string | null }>(
				`/api/offers/${orderHeader.sourceOfferId}`,
			)
			.then((o) => {
				if (cancelled) return;
				const doc = o.documentNumber || o.id.slice(0, 8);
				setInitialOptions((prev) => ({
					...prev,
					offer: { value: o.id, label: o.companyName ? `${o.companyName} — ${doc}` : doc },
				}));
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [isOrder, orderHeader.sourceOfferId]);

	const loadLeads = useCallback(async (query: string) => {
		const res = await api.get<{
			items: Array<{ id: string; companyName: string | null }>;
		}>(`/api/leads?limit=50&status=new${query ? `&search=${encodeURIComponent(query)}` : ""}`);
		return res.items
			.filter((l) => !!l.companyName)
			.map((l) => ({ value: l.id, label: l.companyName as string }));
	}, []);

	const saveNewPerson = async () => {
		if (!header.companyId) return;
		if (!newPerson.firstName.trim() || !newPerson.lastName.trim()) {
			setPersonError(t("requiredField"));
			return;
		}
		setPersonError("");
		setPersonSaving(true);
		try {
			const created = await api.post<{ id: string; firstName: string; lastName: string }>(
				"/api/persons",
				{
					companyId: header.companyId,
					firstName: newPerson.firstName.trim(),
					lastName: newPerson.lastName.trim(),
					phone: newPerson.phone.trim() || undefined,
					email: newPerson.email.trim() || undefined,
				},
			);
			setHeader((prev) => ({ ...prev, contactPersonId: created.id }));
			setInitialOptions((prev) => ({
				...prev,
				person: { value: created.id, label: `${created.firstName} ${created.lastName}` },
			}));
			setPersonModalOpen(false);
			setNewPerson({ firstName: "", lastName: "", phone: "", email: "" });
		} catch (err) {
			setPersonError(err instanceof Error ? err.message : t("error"));
		} finally {
			setPersonSaving(false);
		}
	};

	const loadPersons = useCallback(
		async (query: string) => {
			// Muhatap seçilmeden kişi listesi çekilmemeli: companyId boş gidince API tüm
			// kişileri döndürüyor ve alakasız kişiler listeleniyordu.
			if (!header.companyId) return [];
			const res = await api.get<{
				items: Array<{ id: string; firstName: string; lastName: string }>;
			}>(
				`/api/persons?limit=50${header.companyId ? `&companyId=${header.companyId}` : ""}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			return res.items.map((p) => ({ value: p.id, label: `${p.firstName} ${p.lastName}` }));
		},
		[header.companyId],
	);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			if (!header.companyId && !header.leadId) {
				if (!cancelled) {
					setPriceListType("domestic");
					setPriceListBasis(null);
				}
				return;
			}
			const params = new URLSearchParams();
			if (header.companyId) params.set("companyId", header.companyId);
			if (header.leadId) params.set("leadId", header.leadId);
			try {
				const res = await api.get<{
					type: "domestic" | "export" | "unknown";
					reason?: "country" | "group" | "default";
					country?: string | null;
				}>(`/api/price-list/resolve-type?${params.toString()}`);
				if (!cancelled) {
					setPriceListType(res.type);
					setPriceListBasis({ reason: res.reason ?? "group", country: res.country ?? null });
				}
			} catch {
				if (!cancelled) {
					setPriceListType("domestic");
					setPriceListBasis(null);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [header.companyId, header.leadId]);

	const loadPriceModels = useCallback(
		async (query: string, portable = false) => {
			if (priceListType === "unknown") return [];
			const rows = await api.get<
				Array<{ model: string; standbyKva: string | null; motorModel: string | null }>
			>(
				`/api/price-list?limit=50&type=${priceListType}&category=${portable ? "portable" : "genset"}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			return rows.map((r) => ({
				value: r.model,
				label: `${r.model}${r.standbyKva ? ` — ${Number(r.standbyKva).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} kVA` : ""}${r.motorModel ? ` (${r.motorModel})` : ""}`,
			}));
		},
		[priceListType],
	);

	// Model seçilince: o modelin alternatör/kabin fiyatlarını yükle, SAP ürün seçimini sıfırla.
	const handleSelectModel = async (idx: number, model: string) => {
		setSelectedModelKey((prev) => ({ ...prev, [idx]: model }));
		setSelectedSapKey((prev) => ({ ...prev, [idx]: "" }));
		setLineInfo((prev) => ({ ...prev, [idx]: [] }));
		setLineWarning((prev) => ({ ...prev, [idx]: null }));
		setPanelChecked((prev) => ({ ...prev, [idx]: {} }));
		setPanelSelected((prev) => ({ ...prev, [idx]: {} }));
		setPanelQty((prev) => ({ ...prev, [idx]: {} }));
		setPanelShow((prev) => ({ ...prev, [idx]: {} }));
		setPanelPriced((prev) => ({ ...prev, [idx]: {} }));
		setPanelFolded((prev) => ({ ...prev, [idx]: {} }));
		setPanelSeparate((prev) => ({ ...prev, [idx]: {} }));
		const next = [...lines];
		const line = next[idx];
		if (line) {
			next[idx] = { ...line, itemCode: model, priceListModel: model, stockItemId: "" } as LineItem;
			setLines(next);
		}
		if (!model) {
			setLinePrices((prev) => ({
				...prev,
				[idx]: { alternators: {}, cabins: {}, currency: null, basePrice: null },
			}));
			setLinePanels((prev) => ({ ...prev, [idx]: {} }));
			return;
		}
		if (priceListType === "unknown") {
			setLinePrices((prev) => ({
				...prev,
				[idx]: { alternators: {}, cabins: {}, currency: null, basePrice: null },
			}));
			setLinePanels((prev) => ({ ...prev, [idx]: {} }));
			return;
		}
		try {
			const row = await api.get<{
				alternators: Record<string, number>;
				cabins: Record<string, number>;
				panels: Record<string, Record<string, number>>;
				currency: string | null;
				basePrice: string | null;
			} | null>(
				`/api/price-list/lookup?type=${priceListType}&category=${
					linePortable[idx] ? "portable" : "genset"
				}&model=${encodeURIComponent(model)}`,
			);
			setLinePrices((prev) => ({
				...prev,
				[idx]: {
					alternators: row?.alternators ?? {},
					cabins: row?.cabins ?? {},
					currency: row?.currency ?? null,
					basePrice: row?.basePrice == null ? null : Number(row.basePrice),
				},
			}));
			setLinePanels((prev) => ({ ...prev, [idx]: row?.panels ?? {} }));
			const base = row?.basePrice == null ? 0 : Number(row.basePrice);
			if (base > 0) {
				const label = t("listPrice");
				const group = lines[idx]?.alternativeGroup ?? 0;
				const conv = convertPrice(base, row?.currency ?? null, group);
				if (!conv.ok) setLineWarning((prev) => ({ ...prev, [idx]: t("noRatesForDate") }));
				setLineInfo((prev) => ({
					...prev,
					[idx]: [{ label, price: base, currency: row?.currency ?? null }],
				}));
				setAppliedInfo((prev) => ({ ...prev, [idx]: { [label]: true } }));
				setAppliedAmount((prev) => ({ ...prev, [idx]: { [label]: conv.value } }));
				setLines((prev) => {
					const cur = prev[idx];
					if (!cur) return prev;
					const updated = [...prev];
					updated[idx] = {
						...cur,
						unitPrice: String(Math.round(conv.value * 100) / 100),
						givenUnitPrice: "",
						discountPercent: "0",
					} as LineItem;
					return updated;
				});
			}
			if (row?.currency && row.currency !== header.currency) {
				setHeader((prev) => ({ ...prev, currency: row.currency as string }));
			}
		} catch {
			setLinePrices((prev) => ({
				...prev,
				[idx]: { alternators: {}, cabins: {}, currency: null, basePrice: null },
			}));
			setLinePanels((prev) => ({ ...prev, [idx]: {} }));
		}
	};

	const panelLineAmount = (idx: number, group: string, option: string, qty: string) => {
		const price = option ? (linePanels[idx]?.[group]?.[option] ?? 0) : 0;
		return price * (Number(qty) || 0);
	};

	const syncPanelFold = (
		idx: number,
		group: string,
		option: string,
		qty: string,
		separate = !!panelSeparate[idx]?.[group],
	) => {
		const desired = separate ? 0 : panelLineAmount(idx, group, option, qty);
		const current = panelFolded[idx]?.[group] ?? 0;
		const delta = desired - current;
		if (delta !== 0) {
			const cur = Number(lines[idx]?.unitPrice) || 0;
			updateLine(idx, "unitPrice", String(Math.max(0, Math.round((cur + delta) * 100) / 100)));
			syncDiscount(idx);
			setPanelFolded((prev) => ({ ...prev, [idx]: { ...prev[idx], [group]: desired } }));
		}
	};

	const togglePanelGroup = (idx: number, group: string) => {
		const checked = panelChecked[idx]?.[group] ?? false;
		if (checked) {
			syncPanelFold(idx, group, "", panelQty[idx]?.[group] ?? "1");
			setPanelSelected((prev) => ({ ...prev, [idx]: { ...prev[idx], [group]: "" } }));
			setPanelSeparate((prev) => {
				const { [group]: _removed, ...rest } = prev[idx] ?? {};
				return { ...prev, [idx]: rest };
			});
		} else {
			setPanelQty((prev) => ({
				...prev,
				[idx]: { ...prev[idx], [group]: prev[idx]?.[group] ?? "1" },
			}));
			setPanelShow((prev) => ({
				...prev,
				[idx]: { ...prev[idx], [group]: prev[idx]?.[group] ?? true },
			}));
		}
		setPanelChecked((prev) => ({ ...prev, [idx]: { ...prev[idx], [group]: !checked } }));
	};

	const selectPanelOption = (idx: number, group: string, option: string) => {
		const qty = panelQty[idx]?.[group] ?? "1";
		setPanelSelected((prev) => ({ ...prev, [idx]: { ...prev[idx], [group]: option } }));
		syncPanelFold(idx, group, option, qty);
		if (panelSeparate[idx]?.[group]) {
			const price = option ? (linePanels[idx]?.[group]?.[option] ?? 0) : 0;
			setPanelSeparate((prev) => {
				const cur = prev[idx]?.[group];
				if (!cur) return prev;
				return { ...prev, [idx]: { ...prev[idx], [group]: { ...cur, unitPrice: price } } };
			});
		}
	};

	// Ayrı faturalamada pano fiyatı ana kalemden çıkarılır ve ayrı satıra taşınır (ya da tersi).
	const togglePanelSeparate = (idx: number, group: string) => {
		const option = panelSelected[idx]?.[group] ?? "";
		const qty = panelQty[idx]?.[group] ?? "1";
		const separate = !panelSeparate[idx]?.[group];
		syncPanelFold(idx, group, option, qty, separate);
		setPanelSeparate((prev) => {
			const { [group]: _removed, ...rest } = prev[idx] ?? {};
			if (!separate) return { ...prev, [idx]: rest };
			const price = option ? (linePanels[idx]?.[group]?.[option] ?? 0) : 0;
			return {
				...prev,
				[idx]: {
					...rest,
					[group]: {
						stockItemId: "",
						itemCode: "",
						itemDescription: "",
						unit: "",
						vatGroup: "",
						unitPrice: price,
					},
				},
			};
		});
	};

	const selectSeparateItem = (
		idx: number,
		group: string,
		stockItemId: string,
		option?: { label: string; meta?: Record<string, string> },
	) => {
		setPanelSeparate((prev) => {
			const cur = prev[idx]?.[group];
			if (!cur) return prev;
			return {
				...prev,
				[idx]: {
					...prev[idx],
					[group]: {
						...cur,
						stockItemId,
						itemCode: stockItemId ? (option?.meta?.code ?? "") : "",
						itemDescription: stockItemId ? (option?.meta?.name ?? "") : "",
						unit: stockItemId ? (option?.meta?.uom ?? "") : "",
						vatGroup: stockItemId ? (option?.meta?.vatGroup ?? "") : "",
					},
				},
			};
		});
	};

	const setPanelQtyValue = (idx: number, group: string, qty: string) => {
		const option = panelSelected[idx]?.[group] ?? "";
		setPanelQty((prev) => ({ ...prev, [idx]: { ...prev[idx], [group]: qty } }));
		syncPanelFold(idx, group, option, qty);
	};

	const togglePanelShow = (idx: number, group: string) => {
		const show = !(panelShow[idx]?.[group] ?? true);
		setPanelShow((prev) => ({ ...prev, [idx]: { ...prev[idx], [group]: show } }));
	};

	const togglePanelPriced = (idx: number, group: string) => {
		const priced = !(panelPriced[idx]?.[group] ?? false);
		setPanelPriced((prev) => ({ ...prev, [idx]: { ...prev[idx], [group]: priced } }));
	};

	// Türkçe normalize + tekrar eden harfleri sadeleştir (MARANELLO ≈ MARANELO eşleşsin)
	const norm = (s: string) =>
		s
			.toLocaleUpperCase("tr-TR")
			.replace(/\s+/g, "")
			.replace(/(.)\1+/g, "$1");

	// Ürün adından alternatör + kabin tespit et; eşleşen fiyatları ve (varsa) uyarıyı döndür
	const deriveFromName = (
		name: string,
		idx: number,
		rowOverride?: {
			alternators: Record<string, number>;
			cabins: Record<string, number>;
			currency: string | null;
			basePrice: number | null;
		},
	) => {
		const row = rowOverride ?? linePrices[idx];
		const info: { label: string; price: number; currency: string | null }[] = [];
		let warning: string | null = null;
		if (!row) return { info, warning };

		if (row.basePrice != null && row.basePrice > 0) {
			info.push({ label: t("listPrice"), price: row.basePrice, currency: row.currency });
			return { info, warning };
		}

		const up = name.toLocaleUpperCase("tr-TR");
		const nameNorm = norm(name);

		// Alternatör: sütun adının ilk kelimesi ürün adında geçiyor mu?
		const altEntry = Object.entries(row.alternators).find(([col]) => {
			const firstWord = col.split(/\s+/)[0] ?? col;
			return nameNorm.includes(norm(firstWord));
		});
		if (altEntry) {
			info.push({ label: altEntry[0], price: altEntry[1], currency: row.currency });
		} else {
			// Ürün adında marka geçmiyor olabilir; model kısaltmasından (GJC700-ST gibi) dene.
			const suffixMatch = name.match(/^[A-Z0-9]+-([A-Z]+)\b/i);
			const suffixBrand = suffixMatch
				? ALTERNATOR_SUFFIX_BRANDS[suffixMatch[1]!.toLocaleUpperCase("tr-TR")]
				: undefined;
			const suffixEntry = suffixBrand
				? Object.entries(row.alternators).find(([col]) => {
						const firstWord = col.split(/\s+/)[0] ?? col;
						return norm(firstWord) === norm(suffixBrand);
					})
				: undefined;
			if (suffixEntry) {
				info.push({ label: suffixEntry[0], price: suffixEntry[1], currency: row.currency });
			} else {
				// Üründe alternatör markası belirtilmiş ama listede yok → uyarı
				const m = up.match(/([A-ZÇĞİÖŞÜ]+)\s+ALTERNAT/);
				const brand = m?.[1];
				if (brand) warning = `${brand} ${t("alternatorNotInList")}`;
			}
		}

		const cabinEntries = Object.entries(row.cabins);
		const findCabin = (test: (c: string) => boolean) =>
			cabinEntries.find(([c]) => test(c.toLocaleUpperCase("tr-TR")));
		const isSuperCabin = (c: string) =>
			c.includes("SESS") || c.includes("SILENT") || c.includes("SİLENT");
		const isAnyCabin = (c: string) => c.includes("KAB") || c.includes("CANOPY");
		let cabin: [string, number] | undefined;
		if (
			up.includes("SÜPER SESSİZ") ||
			up.includes("SUPER SESSIZ") ||
			up.includes("SUPER SILENT") ||
			up.includes("SÜPER SİLENT")
		)
			cabin = findCabin(isSuperCabin);
		else if (up.includes("KABİNLİ") || up.includes("KABINLI") || up.includes("CANOPY"))
			cabin = findCabin((c) => isAnyCabin(c) && !isSuperCabin(c));
		if (cabin) info.push({ label: cabin[0], price: cabin[1], currency: row.currency });

		return { info, warning };
	};

	// Düzenlemede fiyat listesi verisi (pano seçenekleri, alternatör/kabin fiyatları)
	// yüklenmiyordu; bu yüzden gömülü pano seçimleri ekranda görünmüyordu. Teklif
	// yüklendikten sonra modelli satırlar için listeyi bir kez çekiyoruz — mevcut pano
	// seçimlerini ve katlanmış tutarları bozmadan (handleSelectModel bunları sıfırlar).
	const panelOptionsLoaded = useRef(false);
	useEffect(() => {
		if ((!isEdit && !copyFromId) || !prefilled || panelOptionsLoaded.current) return;
		if (priceListType === "unknown") return;
		const models = lines
			.map((l, idx) => ({ idx, model: l.priceListModel, name: l.itemDescription }))
			.filter((x) => !!x.model);
		if (models.length === 0) return;
		panelOptionsLoaded.current = true;
		void (async () => {
			for (const { idx, model, name } of models) {
				try {
					const row = await api.get<{
						alternators: Record<string, number>;
						cabins: Record<string, number>;
						panels: Record<string, Record<string, number>>;
						currency: string | null;
						basePrice: string | null;
					} | null>(
						`/api/price-list/lookup?type=${priceListType}&model=${encodeURIComponent(model)}`,
					);
					const prices = {
						alternators: row?.alternators ?? {},
						cabins: row?.cabins ?? {},
						currency: row?.currency ?? null,
						basePrice: row?.basePrice == null ? null : Number(row.basePrice),
					};
					setLinePrices((prev) => ({ ...prev, [idx]: prices }));
					setLinePanels((prev) => ({ ...prev, [idx]: row?.panels ?? {} }));
					const { info } = deriveFromName(name, idx, prices);
					setLineInfo((prev) => ({ ...prev, [idx]: info }));
				} catch {
					// Fiyat listesi okunamazsa satır elle düzenlenebilir durumda kalır.
				}
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEdit, copyFromId, prefilled, priceListType, lines]);

	const loadDirectSapItems = useCallback(async (query: string, category?: string) => {
		const res = await api.get<{
			items: Array<{
				id: string;
				code: string;
				name: string;
				uom: string | null;
				salesVatGroup: string | null;
			}>;
		}>(
			`/api/stock-items?limit=50&activeOnly=true${category ? `&category=${category}` : ""}${
				query ? `&search=${encodeURIComponent(query)}` : ""
			}`,
		);
		return res.items.map((s) => ({
			value: s.id,
			label: `${s.code} — ${s.name}`,
			meta: { code: s.code, name: s.name, uom: s.uom ?? "", vatGroup: s.salesVatGroup ?? "" },
		}));
	}, []);

	// Ayrı faturalanan panoda yalnızca o grubun SAP kalemleri listelenir.
	const panelItemLoaders = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(PANEL_ITEM_CATEGORY).map(([group, category]) => [
					group,
					(query: string) => loadDirectSapItems(query, category),
				]),
			),
		[loadDirectSapItems],
	);

	// Ürünün SAP satış KDV grubunu satıra varsayılan uygula (oran da gruptan)
	const applyDefaultVatGroup = (line: LineItem, vatGroup: string | undefined): LineItem => {
		if (!vatGroup) return line;
		const g = vatGroups.find((v) => v.code === vatGroup);
		return { ...line, vatGroup, ...(g ? { vatRate: String(g.rate) } : {}) };
	};

	const handleSelectDirectSapItem = (
		idx: number,
		stockItemId: string,
		option?: { label: string; meta?: Record<string, string> },
	) => {
		setDirectSapKey((prev) => ({ ...prev, [idx]: stockItemId }));
		if (!option?.meta) return;
		void assignDefaultWarehouse(idx, stockItemId);
		const { code, name, uom, vatGroup } = option.meta;
		setLines((prev) => {
			const next = [...prev];
			const line = next[idx];
			if (!line) return prev;
			// Görünen ad elle değiştirilmediyse (önceki ürünün adıysa) yeni ürünle güncellenir.
			const isAutoDisplay =
				!line.displayDescription || line.displayDescription === line.itemDescription;
			next[idx] = applyDefaultVatGroup(
				{
					...line,
					stockItemId,
					itemCode: code ?? line.itemCode,
					itemDescription: name ?? line.itemDescription,
					displayDescription: isAutoDisplay && name ? name : line.displayDescription,
					unit: uom || line.unit,
					priceListModel: "",
				} as LineItem,
				vatGroup,
			);
			return next;
		});
	};

	const changeLineSource = (idx: number, source: "priceList" | "sap") => {
		setLineSource((prev) => ({ ...prev, [idx]: source }));
		setSelectedModelKey((prev) => ({ ...prev, [idx]: "" }));
		setSelectedSapKey((prev) => ({ ...prev, [idx]: "" }));
		setDirectSapKey((prev) => ({ ...prev, [idx]: "" }));
		setLineInfo((prev) => ({ ...prev, [idx]: [] }));
		setAppliedInfo((prev) => ({ ...prev, [idx]: {} }));
		setLineWarning((prev) => ({ ...prev, [idx]: null }));
		setLinePanels((prev) => ({ ...prev, [idx]: {} }));
		setPanelChecked((prev) => ({ ...prev, [idx]: {} }));
		setPanelSelected((prev) => ({ ...prev, [idx]: {} }));
		setPanelFolded((prev) => ({ ...prev, [idx]: {} }));
		setPanelSeparate((prev) => ({ ...prev, [idx]: {} }));
		setLines((prev) => {
			const next = [...prev];
			const line = next[idx];
			if (!line) return prev;
			next[idx] = {
				...line,
				itemCode: "",
				itemDescription: "",
				stockItemId: "",
				priceListModel: "",
				unitPrice: "",
				givenUnitPrice: "",
				discountPercent: "0",
			} as LineItem;
			return next;
		});
	};

	// Adım 2: Seçilen modeli içeren SAP stok kalemlerini LIKE ile getir.
	const loadSapItemsForModel = useCallback(async (model: string, query: string) => {
		// stok araması tek terim üzerinden çalışır; modelle daralt, kullanıcı yazarsa onu kullan
		const typed = query.trim();
		const modelName = model.trim();
		// SAP'te Xenic kalemleri GJW300X → GJX300 olarak yeniden adlandırıldı; fiyat listesi eski adı taşıyor
		const xenic = /^GJW(\d+)X$/i.exec(modelName);
		const modelNames = xenic ? [modelName, `GJX${xenic[1]}`] : [modelName];
		// GJD7000S-3 ile GJD7000S3 aynı model: ayraçtan önceki kökle ara, ayraç farkını regex tolere eder
		const modelPartsList = modelNames
			.map((m) => m.split(/[^0-9A-Za-z]+/).filter(Boolean))
			.filter((parts) => parts.length > 0);
		const searches = typed
			? [typed]
			: modelPartsList.length
				? modelPartsList.map((parts) => parts[0] as string)
				: [modelName];
		type StockRow = {
			id: string;
			code: string;
			name: string;
			uom: string | null;
			salesVatGroup: string | null;
		};
		const results = await Promise.all(
			searches.map((search) =>
				api.get<{ items: StockRow[] }>(
					`/api/stock-items?limit=200&activeOnly=true${search ? `&search=${encodeURIComponent(search)}` : ""}`,
				),
			),
		);
		const seen = new Set<string>();
		let items = results
			.flatMap((r) => r.items)
			.filter((s) => (seen.has(s.id) ? false : (seen.add(s.id), true)));
		// Kullanıcı serbest arama yapmadıysa: modeli sınırla (GJP10 → GJP10-..., GJP100 hariç)
		if (!typed && modelPartsList.length) {
			// isim modelle başlamalı; ardından rakam/harf ya da -3 gibi varyant eki gelmemeli (GJP10- ✓, GJP100 ✗, GJD7000S-3 ✗)
			const res = modelPartsList.map(
				(parts) => new RegExp(`^${parts.join("[^0-9A-Za-z]?")}(?![0-9A-Za-z]|-[0-9])`, "i"),
			);
			const filtered = items.filter((s) => res.some((re) => re.test(s.name.trim())));
			if (filtered.length > 0) items = filtered;
		}
		return items.map((s) => ({
			value: s.id,
			label: `${s.code} — ${s.name}`,
			meta: { code: s.code, name: s.name, uom: s.uom ?? "", vatGroup: s.salesVatGroup ?? "" },
		}));
	}, []);

	const handleSelectSapItem = (
		idx: number,
		stockItemId: string,
		option?: { label: string; meta?: Record<string, string> },
	) => {
		setSelectedSapKey((prev) => ({ ...prev, [idx]: stockItemId }));
		if (!option?.meta) return;
		void assignDefaultWarehouse(idx, stockItemId);
		const { code, name, uom, vatGroup } = option.meta;
		const next = [...lines];
		const line = next[idx];
		if (!line) return;
		// Alternatör + kabin fiyatlarını otomatik hesapla ve birim fiyata ekle
		const { info, warning: derivedWarning } = deriveFromName(name ?? "", idx);
		let warning = derivedWarning;
		const group = line.alternativeGroup ?? 0;
		const applied: Record<string, boolean> = {};
		const amounts: Record<string, number> = {};
		for (const i of info) {
			const conv = convertPrice(i.price, i.currency, group);
			if (!conv.ok) warning = t("noRatesForDate");
			applied[i.label] = true;
			amounts[i.label] = conv.value;
		}
		const autoTotal = Object.values(amounts).reduce((sum, v) => sum + v, 0);
		next[idx] = applyDefaultVatGroup(
			{
				...line,
				stockItemId,
				itemCode: code ?? line.itemCode,
				itemDescription: name ?? line.itemDescription,
				unit: uom || line.unit,
				unitPrice: autoTotal > 0 ? String(Math.round(autoTotal * 100) / 100) : line.unitPrice,
				givenUnitPrice: "",
				discountPercent: "0",
			} as LineItem,
			vatGroup,
		);
		setLines(next);
		setLineInfo((prev) => ({ ...prev, [idx]: info }));
		setAppliedInfo((prev) => ({ ...prev, [idx]: applied }));
		setAppliedAmount((prev) => ({ ...prev, [idx]: amounts }));
		setLineWarning((prev) => ({ ...prev, [idx]: warning }));
	};

	// Bilgi rozetini birim fiyata ekle / çıkar (toggle)
	const toggleInfoPrice = (
		idx: number,
		info: { label: string; price: number; currency?: string | null },
	) => {
		const isApplied = appliedInfo[idx]?.[info.label] ?? false;
		const group = lines[idx]?.alternativeGroup ?? 0;
		const current = Number(lines[idx]?.unitPrice) || 0;
		if (isApplied) {
			// Kaldır: eklenirken kaydedilen gerçek tutarı düş
			const applied = appliedAmount[idx]?.[info.label] ?? info.price;
			updateLine(
				idx,
				"unitPrice",
				String(Math.max(0, Math.round((current - applied) * 100) / 100)),
			);
			setAppliedAmount((prev) => {
				const next = { ...(prev[idx] ?? {}) };
				delete next[info.label];
				return { ...prev, [idx]: next };
			});
		} else {
			const conv = convertPrice(info.price, info.currency ?? null, group);
			if (!conv.ok) {
				setLineWarning((prev) => ({ ...prev, [idx]: t("noRatesForDate") }));
			}
			updateLine(
				idx,
				"unitPrice",
				String(Math.max(0, Math.round((current + conv.value) * 100) / 100)),
			);
			setAppliedAmount((prev) => ({
				...prev,
				[idx]: { ...prev[idx], [info.label]: conv.value },
			}));
		}
		setAppliedInfo((prev) => ({
			...prev,
			[idx]: { ...prev[idx], [info.label]: !isApplied },
		}));
		syncDiscount(idx);
	};

	const submitOrder = () => {
		if (!orderId && !isOrderCopy) return;
		setFormError("");
		const errors: Record<string, string> = {};
		const missingLabels: string[] = [];
		if (!header.companyId) {
			errors.companyId = t("requiredField");
			missingLabels.push(t("company"));
		}
		if (!orderHeader.orderDate) {
			errors.orderDate = t("requiredField");
			missingLabels.push(t("orderDate"));
		}
		if (!orderHeader.deliveryDate) {
			errors.deliveryDate = t("requiredField");
			missingLabels.push(t("deliveryDate"));
		}
		if (!header.paymentTerms.trim()) {
			errors.paymentTerms = t("requiredField");
			missingLabels.push(t("paymentTerms"));
		}
		setFieldErrors(errors);
		if (missingLabels.length > 0) {
			const message = t("missingFields", { fields: missingLabels.join(", ") });
			setFormError(message);
			showToast(message, "error");
			return;
		}
		const missingStockItem = lines.findIndex((l) => !l.isSection && !l.stockItemId);
		if (missingStockItem !== -1) {
			const message = t("stockItemRequiredLine", { line: missingStockItem + 1 });
			setFormError(message);
			showToast(message, "error");
			return;
		}
		const missingSeparate = findIncompleteSeparatePanel();
		if (missingSeparate) {
			const message = t("separatePanelItemRequired", {
				line: missingSeparate.idx + 1,
				group: missingSeparate.group,
			});
			setFormError(message);
			showToast(message, "error");
			return;
		}
		const invalidLine = lines.findIndex((l) => !l.quantity || Number(l.quantity) <= 0);
		if (invalidLine !== -1) {
			const message = t("quantityRequiredLine", { line: invalidLine + 1 });
			setFormError(message);
			showToast(message, "error");
			return;
		}

		const orderLines = lines.flatMap((l, idx) => {
			if (l.isSection) {
				const title = l.displayDescription || l.itemDescription || undefined;
				return [
					{
						itemDescription: title,
						displayDescription: title,
						quantity: Number(l.quantity) || 1,
						unit: l.unit || undefined,
						unitPrice: Number(l.unitPrice) || 0,
						vatRate: l.vatRate ? Number(l.vatRate) : undefined,
						vatGroup: l.vatGroup || undefined,
						discountPercent: l.discountPercent ? Number(l.discountPercent) : undefined,
						lineTotal: calcLineTotal(l),
						isCustomDesign: l.isCustomDesign,
					},
				];
			}
			const priceComponents = buildPriceComponents(idx);
			const parent = {
				itemCode: l.itemCode || undefined,
				itemDescription: l.itemDescription || undefined,
				displayDescription: l.displayDescription || undefined,
				lineDetail: l.lineDetail || undefined,
				quantity: Number(l.quantity) || 1,
				unit: l.unit || undefined,
				unitPrice: Number(l.unitPrice) || 0,
				vatRate: l.vatRate ? Number(l.vatRate) : undefined,
				vatGroup: l.vatGroup || undefined,
				discountPercent: l.discountPercent ? Number(l.discountPercent) : undefined,
				lineTotal: calcLineTotal(l),
				notes: l.notes || undefined,
				atsDescription: l.atsDescription || undefined,
				tmsDescription: l.tmsDescription || undefined,
				factorySpecialNotes: l.factorySpecialNotes || undefined,
				generalNotes: l.generalNotes || undefined,
				priceListModel: l.priceListModel || selectedModelKey[idx] || undefined,
				priceComponents: priceComponents.length ? priceComponents : undefined,
				isCustomDesign: l.isCustomDesign,
				stockItemId: l.stockItemId || undefined,
				warehouseId: l.warehouseId || undefined,
			};
			const panels = buildPanelLines(idx).map((p) => ({
				itemDescription: p.itemDescription,
				displayDescription: p.itemDescription,
				quantity: p.quantity,
				unit: p.unit,
				unitPrice: 0,
				vatRate: p.vatRate,
				vatGroup: l.vatGroup || undefined,
				discountPercent: 0,
				lineTotal: 0,
				isCustomDesign: l.isCustomDesign,
				warehouseId: l.warehouseId || undefined,
			}));
			const separate = buildSeparateLines(idx).map((p) => ({
				...p,
				warehouseId: l.warehouseId || undefined,
			}));
			return [parent, ...panels, ...separate];
		});

		const data = {
			companyId: header.companyId || undefined,
			currency: currencyOfGroup(0),
			sourceOfferId: orderHeader.sourceOfferId || undefined,
			status: orderHeader.status,
			orderDate: orderHeader.orderDate,
			deliveryDate: orderHeader.deliveryDate,
			paymentTerms: header.paymentTerms.trim(),
			shippingAddress: orderHeader.shippingAddress || undefined,
			shippingAddressId: orderHeader.shippingAddressId || undefined,
			notes: header.notes,
			contactPersonId: header.contactPersonId || undefined,
			subtotal,
			vatTotal,
			total,
			lines: orderLines.map((line, i) => ({ ...line, lineNumber: i + 1 })),
		};
		const onError = (err: unknown) => {
			const message = err instanceof Error ? err.message : t("error");
			setFormError(message);
			showToast(message, "error");
		};

		if (!orderId) {
			createOrder.mutate(
				{ ...copyExtras, ...data, assignedTo: onBehalfOf || undefined },
				{
					onSuccess: async (created) => {
						if (pendingFiles.length > 0) {
							await uploadOrderAttachments.mutateAsync({
								orderId: created.id,
								files: pendingFiles,
							});
						}
						navigate({ to: "/orders/$id", params: { id: created.id } });
					},
					onError,
				},
			);
			return;
		}

		updateOrder.mutate(
			{ id: orderId, data },
			{
				onSuccess: async (updated) => {
					if (pendingFiles.length > 0) {
						await uploadOrderAttachments.mutateAsync({ orderId: updated.id, files: pendingFiles });
					}
					for (const item of removedOrderAttachments) {
						await deleteOrderAttachment.mutateAsync({ orderId: updated.id, item });
					}
					navigate({ to: "/orders/$id", params: { id: updated.id } });
				},
				onError,
			},
		);
	};

	const submit = () => {
		if (isOrder) {
			submitOrder();
			return;
		}
		setFormError("");
		const errors: Record<string, string> = {};
		// Eksik alanların adları toplanır: sayfa başındaki uyarı kaydırılmadan görünmediği
		// için kullanıcıya neyin eksik olduğunu bildirimde de söyleriz.
		const missingLabels: string[] = [];
		// Muhatap ya da potansiyel müşteriden biri seçilmiş olmalı.
		if (!header.companyId && !header.leadId) {
			errors.companyId = t("companyOrLeadRequired");
			missingLabels.push(t("company"));
		}
		if (!header.offerDate) {
			errors.offerDate = t("requiredField");
			missingLabels.push(t("offerDate"));
		}
		if (!header.validUntil) {
			errors.validUntil = t("requiredField");
			missingLabels.push(t("validUntil"));
		}
		setFieldErrors(errors);
		if (missingLabels.length > 0) {
			const message = t("missingFields", { fields: missingLabels.join(", ") });
			setFormError(message);
			showToast(message, "error");
			return;
		}
		// Başlık (section) satırları SAP'ye kalem olarak gitmiyor; stok kalemi zorunlu değil.
		const missingStockItem = lines.findIndex((l) => !l.isSection && !l.stockItemId);
		if (missingStockItem !== -1) {
			const message = t("stockItemRequiredLine", { line: missingStockItem + 1 });
			setFormError(message);
			showToast(message, "error");
			return;
		}
		const missingSeparate = findIncompleteSeparatePanel();
		if (missingSeparate) {
			const message = t("separatePanelItemRequired", {
				line: missingSeparate.idx + 1,
				group: missingSeparate.group,
			});
			setFormError(message);
			showToast(message, "error");
			return;
		}
		const invalidLine = lines.findIndex((l) => !l.quantity || Number(l.quantity) <= 0);
		if (invalidLine !== -1) {
			const message = t("quantityRequiredLine", { line: invalidLine + 1 });
			setFormError(message);
			showToast(message, "error");
			return;
		}
		const payload = {
			...(isEdit ? {} : copyExtras),
			...header,
			// Teklifin para birimi = ilk alternatifin para birimi (başlıkta alan yok).
			currency: offerCurrency,
			validUntil: header.validUntil || undefined,
			offerDate: header.offerDate || undefined,
			commitmentDate: header.commitmentDate || undefined,
			commitmentValue: header.commitmentValue || undefined,
			commitmentUnit: header.commitmentValue ? header.commitmentUnit : undefined,
			isTaxIncluded: header.isTaxIncluded,
			showGtipInPdf: header.showGtipInPdf,
			pdfNotes: isEdit ? header.pdfNotes : header.pdfNotes || undefined,
			buyerExtraInfo: isEdit ? header.buyerExtraInfo : header.buyerExtraInfo || undefined,
			contractNo: isEdit ? header.contractNo : header.contractNo || undefined,
			contractDate: header.contractDate || undefined,
			// legacy single-language columns kept in sync (tr) for SAP outbound sync
			termsCommissioning: termsI18n.commissioning?.tr || undefined,
			termsAssembly: termsI18n.assembly?.tr || undefined,
			termsVat: termsI18n.vat?.tr || undefined,
			termsQuality: termsI18n.quality?.tr || undefined,
			termsValidity: termsI18n.validity?.tr || undefined,
			termsWarranty: termsI18n.warranty?.tr || undefined,
			termsPayment: termsI18n.payment?.tr || undefined,
			termsDelivery: termsI18n.delivery?.tr || undefined,
			termsPaymentMethod: termsI18n.paymentMethod?.tr || undefined,
			termsI18n,
			companyId: header.companyId || undefined,
			companySapAccountId: header.companySapAccountId || null,
			leadId: header.leadId || undefined,
			contactPersonId: header.contactPersonId || undefined,
			assignedTo: !isOrder && onBehalfOf ? onBehalfOf : undefined,
			lines: lines
				.flatMap((l, idx) => {
					const parentLine = {
						itemCode: l.itemCode || undefined,
						itemDescription: l.itemDescription || undefined,
						displayDescription: l.displayDescription || undefined,
						quantity: Number(l.quantity) || 1,
						unit: l.unit || undefined,
						unitPrice: Number(l.unitPrice) || 0,
						givenUnitPrice: l.givenUnitPrice ? Number(l.givenUnitPrice) : undefined,
						vatRate: l.vatRate ? Number(l.vatRate) : undefined,
						vatGroup: l.vatGroup || undefined,
						discountPercent: l.discountPercent ? Number(l.discountPercent) : undefined,
						lineTotal: calcLineTotal(l),
						notes: l.notes || undefined,
						factorySpecialNotes: l.factorySpecialNotes || undefined,
						generalNotes: l.generalNotes || undefined,
						priceListModel: l.priceListModel || selectedModelKey[idx] || undefined,
						commitmentValue: l.commitmentValue || undefined,
						commitmentUnit: l.commitmentValue
							? (l.commitmentUnit as "day" | "week" | "month")
							: undefined,
						stockItemId: l.stockItemId || undefined,
						showInPdf: l.showInPdf,
						showPriceInPdf: l.showPriceInPdf,
						showDiscountInPdf: l.showDiscountInPdf,
						sendDatasheetInEmail: l.sendDatasheetInEmail,
						isCustomDesign: l.isCustomDesign,
						lineDetail: l.lineDetail || undefined,
						alternativeGroup: l.alternativeGroup,
						alternativeCurrency: currencyOfGroup(l.alternativeGroup),
						isSection: l.isSection,
						sectionItems: l.isSection
							? l.sectionItems
									// Adı boş bırakılmış alt kalemin de adedi/fiyatı korunmalı; aksi halde tutar
									// birim fiyata katlanmış olarak kalır ama düzenlemede kalem kaybolur.
									.filter(
										(it) => it.name.trim() || Number(it.quantity) > 0 || Number(it.unitPrice) > 0,
									)
									.map((it) => ({
										name: it.name.trim(),
										quantity: Number(it.quantity) || 0,
										unitPrice: Number(it.unitPrice) || 0,
									}))
							: undefined,
						priceComponents: buildPriceComponents(idx).length
							? buildPriceComponents(idx)
							: undefined,
					};
					const panelLines = buildPanelLines(idx).map((p) => ({
						itemCode: undefined,
						itemDescription: p.itemDescription,
						displayDescription: p.itemDescription,
						quantity: p.quantity,
						unit: p.unit,
						unitPrice: p.unitPrice,
						vatRate: p.vatRate || undefined,
						discountPercent: undefined,
						lineTotal: p.quantity * p.unitPrice * (1 + p.vatRate / 100),
						notes: undefined,
						priceListModel: undefined,
						commitmentValue: l.commitmentValue || undefined,
						commitmentUnit: l.commitmentValue
							? (l.commitmentUnit as "day" | "week" | "month")
							: undefined,
						stockItemId: undefined,
						showInPdf: true,
						showPriceInPdf: p.showPriceInPdf,
						isCustomDesign: l.isCustomDesign,
						lineDetail: undefined,
						alternativeGroup: l.alternativeGroup,
						alternativeCurrency: currencyOfGroup(l.alternativeGroup),
					}));
					const separateLines = buildSeparateLines(idx).map((p) => ({
						...p,
						commitmentValue: l.commitmentValue || undefined,
						commitmentUnit: l.commitmentValue
							? (l.commitmentUnit as "day" | "week" | "month")
							: undefined,
						showInPdf: true,
						showPriceInPdf: true,
						showDiscountInPdf: false,
						sendDatasheetInEmail: false,
						alternativeGroup: l.alternativeGroup,
						alternativeCurrency: currencyOfGroup(l.alternativeGroup),
					}));
					return [parentLine, ...panelLines, ...separateLines];
				})
				.map((line, i) => ({ ...line, lineNumber: i + 1 })),
		};

		const onError = (err: unknown) => {
			const message = err instanceof Error ? err.message : t("error");
			setFormError(message);
			showToast(message, "error");
		};

		if (isEdit && editId) {
			updateOffer.mutate(
				{ id: editId, data: payload },
				{
					// Kaydetme sonucu artık toast ile bildiriliyor (SAP'ye yansıma durumu dahil),
					// bu yüzden bloklayan alert kaldırıldı.
					onSuccess: async (updated) => {
						if (pendingFiles.length > 0) {
							await uploadAttachments.mutateAsync({ offerId: updated.id, files: pendingFiles });
						}
						for (const item of [...removedAttachments].sort((a, b) => b.index - a.index)) {
							await deleteAttachment.mutateAsync({ offerId: updated.id, index: item.index });
						}
						navigate({ to: "/offers/$id", params: { id: updated.id } });
					},
					onError,
				},
			);
		} else {
			createOffer.mutate(payload, {
				onSuccess: async (created) => {
					if (pendingFiles.length > 0) {
						await uploadAttachments.mutateAsync({ offerId: created.id, files: pendingFiles });
					}
					draft.clear();
					alert(t("createSuccess"));
					navigate({
						to: "/offers",
						search: {
							page: 1,
							size: 20,
							status: "",
							search: "",
							companyId: undefined,
							assignedTo: undefined,
							offerDateFrom: "",
							offerDateTo: "",
							createdAtFrom: "",
							createdAtTo: "",
							sort: "",
							dir: "desc" as const,
						},
					});
				},
				onError,
			});
		}
	};

	const isNewDocument = !isEdit || isOrderCopy;
	const onBehalfOfField = canDelegate && (!isOrder || isOrderCopy) && (
		<FormField label={t("onBehalfOf")}>
			<SearchableSelect
				options={delegatableUsers.map((u) => ({ value: u.id, label: u.name }))}
				value={onBehalfOf}
				onChange={(val) => {
					if (val === onBehalfOf) return;
					setOnBehalfOf(val);
					if (!isNewDocument) return;
					setHeader({
						...header,
						companyId: "",
						companySapAccountId: "",
						contactPersonId: "",
					});
					setInitialOptions((prev) => ({
						...prev,
						company: undefined,
						person: undefined,
						offer: undefined,
					}));
					if (isOrder) {
						setOrderHeader({
							...orderHeader,
							sourceOfferId: "",
							shippingAddressId: "",
							shippingAddress: "",
						});
					}
				}}
				placeholder={t("onBehalfOfSelf")}
				clearable={isNewDocument}
			/>
		</FormField>
	);

	return (
		<div>
			<PageHeader
				title={
					isOrderCopy
						? t("newOrder")
						: isOrder
							? t("editOrder")
							: isEdit
								? t("editOffer")
								: t("createOffer")
				}
			/>

			{draft.showPrompt && (
				<Card>
					<div className={styles.draftPrompt}>
						<span>Kaydedilmemiş bir taslak bulundu. Verileriniz geri yüklensin mi?</span>
						<div className={styles.draftPromptActions}>
							<Button onClick={restoreDraft}>Evet</Button>
							<Button variant="ghost" onClick={draft.discardDraft}>
								Hayır
							</Button>
						</div>
					</div>
				</Card>
			)}

			<div className={styles.grid}>
				{isOrder ? (
					<Card header={t("generalInfo")}>
						<div className={styles.formGrid}>
							{formError && <div className={styles.formError}>{formError}</div>}
							{onBehalfOfField}
							<FormField label={t("company")} required>
								<SearchableSelect
									value={header.companySapAccountId || header.companyId}
									onChange={(val, option) => {
										const currency = option?.meta?.currency || currencyOfGroup(0);
										setHeader({
											...header,
											companyId: option?.meta?.companyId ?? val,
											companySapAccountId: option?.meta?.companySapAccountId || "",
											currency,
											contactPersonId: "",
										});
										setGroupCurrency({ 0: currency });
										setOrderHeader({
											...orderHeader,
											sourceOfferId: "",
											shippingAddressId: "",
											shippingAddress: "",
										});
										setInitialOptions((prev) => ({ ...prev, person: undefined, offer: undefined }));
									}}
									loadOptions={loadCompanies}
									refetchKey={onBehalfOf}
									initialOption={initialOptions.company}
									placeholder={t("selectCompany")}
									// Kaydedilmiş muhatap değiştirilemez; muhatapsız taslakta ilk seçim yapılabilir.
									disabled={!isOrderCopy && !!orderDetail.data?.companyId}
									clearable={isOrderCopy || !orderDetail.data?.companyId}
								/>
							</FormField>
							<FormField label={t("authorizedPerson")}>
								<SearchableSelect
									key={header.companyId || "no-company"}
									value={header.contactPersonId}
									onChange={(val) => setHeader({ ...header, contactPersonId: val })}
									loadOptions={loadPersons}
									initialOption={initialOptions.person}
									placeholder={t("selectPerson")}
									disabled={!header.companyId}
									clearable
								/>
							</FormField>
							<FormField label={t("offer")}>
								<SearchableSelect
									value={orderHeader.sourceOfferId}
									onChange={(val) => setOrderHeader({ ...orderHeader, sourceOfferId: val })}
									loadOptions={loadOrderOffers}
									refetchKey={header.companyId}
									initialOption={initialOptions.offer}
									placeholder={t("selectOffer")}
									disabled={!header.companyId}
									clearable
								/>
							</FormField>
							<FormField label={t("orderDate")} required>
								<Input
									type="date"
									value={orderHeader.orderDate}
									onChange={(e) => setOrderHeader({ ...orderHeader, orderDate: e.target.value })}
									error={fieldErrors.orderDate}
								/>
							</FormField>
							<FormField label={t("deliveryDate")} required>
								<Input
									type="date"
									value={orderHeader.deliveryDate}
									onChange={(e) => setOrderHeader({ ...orderHeader, deliveryDate: e.target.value })}
									error={fieldErrors.deliveryDate}
								/>
							</FormField>
							<FormField label={t("currency")}>
								<Input value={currencyOfGroup(0)} readOnly disabled />
							</FormField>
							<FormField label={t("paymentTerms")} required>
								<Input
									value={header.paymentTerms}
									onChange={(e) => setHeader({ ...header, paymentTerms: e.target.value })}
									error={fieldErrors.paymentTerms}
								/>
							</FormField>
							<FormField label={t("shippingAddress")}>
								{header.companyId ? (
									<ShippingAddressPicker
										companyId={header.companyId}
										value={orderHeader.shippingAddressId}
										onChange={(addressId, formatted) =>
											setOrderHeader({
												...orderHeader,
												shippingAddressId: addressId,
												shippingAddress: formatted,
											})
										}
									/>
								) : (
									<Input value="" readOnly placeholder={t("selectCompanyFirst")} />
								)}
							</FormField>
							<FormField label={`${t("orderGeneralDescription")} (${header.notes.length}/254)`}>
								<textarea
									className={styles.textarea}
									rows={1}
									maxLength={254}
									value={header.notes}
									onChange={(e) => setHeader({ ...header, notes: e.target.value })}
								/>
							</FormField>
							<FormField label={t("attachments")}>
								<div className={styles.attachmentsDropzoneWrap}>
									<FileDropzone files={pendingFiles} onChange={setPendingFiles} />
								</div>
								{(orderAttachments.data?.items.length ?? 0) > 0 && orderId && (
									<div className={styles.existingAttachments}>
										{orderAttachments
											.data!.items.filter((item) => !isRemovedOrderAttachment(item))
											.map((item) => (
												<div
													key={`${item.source}-${item.source === "sap" ? item.line : item.index}`}
													className={styles.existingAttachmentLink}
												>
													<a
														href={orderAttachmentDownloadUrl(orderId, item, { inline: true })}
														target="_blank"
														rel="noreferrer"
														className={styles.existingAttachmentName}
													>
														{item.name}
													</a>
													<a
														href={orderAttachmentDownloadUrl(orderId, item)}
														aria-label={t("download")}
														className={styles.existingAttachmentDownload}
													>
														<Download size={14} />
													</a>
													<button
														type="button"
														aria-label={t("remove")}
														className={styles.existingAttachmentRemove}
														onClick={() => setRemovedOrderAttachments((prev) => [...prev, item])}
													>
														<X size={14} />
													</button>
												</div>
											))}
									</div>
								)}
							</FormField>
							<div className={styles.checkboxFieldWrapper}>
								<label className={styles.checkboxField}>
									<input
										type="checkbox"
										checked={lines.length > 0 && lines.every((l) => l.isCustomDesign)}
										onChange={(e) => {
											const checked = e.target.checked;
											setLines((prev) => prev.map((l) => ({ ...l, isCustomDesign: checked })));
										}}
									/>
									<span>{t("allCustomDesign")}</span>
								</label>
							</div>
						</div>
						<FormField label={t("status")}>
							<Select
								options={[
									...(!["confirmed", "closed", "cancelled"].includes(orderHeader.status)
										? [
												{
													value: orderHeader.status,
													label: ORDER_STATUS_LABELS[orderHeader.status] ?? "Açık",
												},
											]
										: []),
									{ value: "confirmed", label: "Açık" },
									{ value: "closed", label: "Kapalı" },
									{ value: "cancelled", label: "İptal Edildi" },
								]}
								value={orderHeader.status}
								onChange={(e) => setOrderHeader({ ...orderHeader, status: e.target.value })}
							/>
						</FormField>
					</Card>
				) : (
					<>
						<Card header={t("generalInfo")}>
							<div className={styles.formGrid}>
								{formError && <div className={styles.formError}>{formError}</div>}
								{priceListType === "unknown" && (
									<div className={styles.formError} style={{ gridColumn: "1 / -1" }}>
										{t("priceListUndetermined")}{" "}
										<Button
											type="button"
											size="sm"
											variant="secondary"
											onClick={() => setPriceListType("domestic")}
										>
											{t("continueWithDomestic")}
										</Button>
									</div>
								)}
								{!isEdit && priceListBasis && priceListType !== "unknown" && (
									<div className={styles.priceListInfo}>
										{priceListType === "export"
											? t("priceListInfoExport")
											: t("priceListInfoDomestic")}
										{priceListType === "export" && priceListBasis.reason === "country"
											? ` · ${t("priceListBasisCountry", { country: priceListBasis.country ?? "" })}`
											: priceListBasis.reason === "group"
												? ` · ${t("priceListBasisGroup")}`
												: ""}
									</div>
								)}
								{!isOrder && onBehalfOfField}
								<FormField label={t("company")} required>
									<SearchableSelect
										value={header.companySapAccountId || header.companyId}
										onChange={(val, option) => {
											// Aynı firmanın birden fazla para birimi carisi olabilir; option.meta
											// gerçekte hangi firma+hesabın seçildiğini taşır (value sadece o hesabın
											// benzersiz kimliğidir). Muhatap ve potansiyel müşteri birbirini dışlar.
											setHeader({
												...header,
												companyId: option?.meta?.companyId ?? val,
												companySapAccountId: option?.meta?.companySapAccountId || "",
												currency: option?.meta?.currency || header.currency,
												leadId: "",
												contactPersonId: "",
											});
											setInitialOptions((prev) => ({
												...prev,
												person: undefined,
											}));
										}}
										loadOptions={loadCompanies}
										refetchKey={onBehalfOf}
										initialOption={initialOptions.company}
										placeholder={t("selectCompany")}
										disabled={!!header.leadId}
										clearable
									/>
									{sapAccountChangeWarning && (
										<div className={styles.formError} style={{ marginTop: 4 }}>
											{sapAccountChangeWarning}
										</div>
									)}
								</FormField>
								<FormField label={t("lead")}>
									<SearchableSelect
										value={header.leadId}
										onChange={(val) =>
											setHeader({
												...header,
												leadId: val,
												companyId: "",
												companySapAccountId: "",
												contactPersonId: "",
											})
										}
										loadOptions={loadLeads}
										initialOption={initialOptions.lead}
										placeholder={t("selectLead")}
										disabled={!!header.companyId}
										clearable
									/>
									{!isOrder && header.leadId && !header.companyId && (
										<div className={styles.formError} style={{ marginTop: 4, fontSize: 12 }}>
											{t("leadOfferNotSentToSap")}
											{leadCompanyMatches.length > 0 && (
												<div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
													<span>{t("leadSameNameCompanyFound")}</span>
													{leadCompanyMatches.map((o) => (
														<div key={o.value}>
															<Button
																type="button"
																size="sm"
																variant="secondary"
																onClick={() => selectSuggestedCompany(o)}
															>
																{t("useAsCompany", { name: o.label })}
															</Button>
														</div>
													))}
												</div>
											)}
										</div>
									)}
								</FormField>
								<FormField label={t("validUntil")} required>
									<Input
										type="date"
										value={header.validUntil}
										onChange={(e) => {
											const days = e.target.value
												? Math.round(
														DateTime.fromISO(e.target.value).diff(
															DateTime.now().startOf("day"),
															"days",
														).days,
													)
												: null;
											setHeader({ ...header, validUntil: e.target.value });
											if (days !== null && days > 0) {
												setTermsI18n((prev) => ({
													...prev,
													validity: {
														...prev.validity,
														tr: `Fiyatlarımız ${days} gün için geçerlidir.`,
													},
												}));
											}
										}}
										error={fieldErrors.validUntil}
									/>
								</FormField>
								<FormField label={t("offerDate")} required>
									<Input
										type="date"
										value={header.offerDate}
										onChange={(e) => setHeader({ ...header, offerDate: e.target.value })}
										error={fieldErrors.offerDate}
									/>
								</FormField>
								<FormField label={t("authorizedPerson")}>
									<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
										<div style={{ flex: 1, minWidth: 0 }}>
											<SearchableSelect
												key={header.companyId || "no-company"}
												value={header.contactPersonId}
												onChange={(val) => setHeader({ ...header, contactPersonId: val })}
												loadOptions={loadPersons}
												initialOption={initialOptions.person}
												placeholder={t("selectPerson")}
												disabled={!header.companyId}
												clearable
											/>
										</div>
										<Button
											type="button"
											variant="secondary"
											disabled={!header.companyId}
											onClick={() => setPersonModalOpen(true)}
										>
											{t("addPerson")}
										</Button>
									</div>
								</FormField>
								<FormField label={`${t("factoryNotes")} (${header.notes.length}/254)`}>
									<textarea
										className={styles.textarea}
										rows={1}
										maxLength={254}
										value={header.notes}
										onChange={(e) => setHeader({ ...header, notes: e.target.value })}
									/>
								</FormField>
								<FormField label={t("pdfNotes")}>
									<Input
										value={header.pdfNotes}
										onChange={(e) => setHeader({ ...header, pdfNotes: e.target.value })}
									/>
								</FormField>
								<FormField label={t("commitmentDuration")}>
									<div className={styles.durationWithCheckbox}>
										<div className={styles.durationRow}>
											<Input
												type="text"
												value={header.commitmentValue}
												onChange={(e) => setHeader({ ...header, commitmentValue: e.target.value })}
												disabled={header.offerOrderDeliveryType === STOCK_DELIVERY_TYPE}
												style={{ flex: 1, minWidth: 0 }}
											/>
											<Select
												options={[
													{ value: "day", label: t("unitDay") },
													{ value: "week", label: t("unitWeek") },
													{ value: "month", label: t("unitMonth") },
												]}
												value={header.commitmentUnit}
												onChange={(e) => setHeader({ ...header, commitmentUnit: e.target.value })}
												disabled={header.offerOrderDeliveryType === STOCK_DELIVERY_TYPE}
												style={{ width: 110, flexShrink: 0 }}
											/>
										</div>
										<label className={styles.checkboxField}>
											<input
												type="checkbox"
												checked={header.offerOrderDeliveryType === STOCK_DELIVERY_TYPE}
												onChange={(e) =>
													setHeader({
														...header,
														offerOrderDeliveryType: e.target.checked ? STOCK_DELIVERY_TYPE : "",
														commitmentValue: e.target.checked ? "" : header.commitmentValue,
													})
												}
											/>
											<span>{t("stockDelivery")}</span>
										</label>
									</div>
								</FormField>
								<FormField label={t("contractNo")}>
									<Input
										value={header.contractNo}
										onChange={(e) => setHeader({ ...header, contractNo: e.target.value })}
									/>
								</FormField>
								<FormField label={t("contractDate")}>
									<Input
										type="date"
										value={header.contractDate}
										onChange={(e) => setHeader({ ...header, contractDate: e.target.value })}
									/>
								</FormField>
								<div className={styles.checkboxFieldWrapper}>
									<label className={styles.checkboxField}>
										<input
											type="checkbox"
											checked={header.isTaxIncluded}
											onChange={(e) => {
												setHeader({ ...header, isTaxIncluded: e.target.checked });
												setTermsI18n((prev) => ({
													...prev,
													vat: {
														...prev.vat,
														tr: e.target.checked ? "Fiyata dahildir." : "Fiyata dahil değildir.",
													},
												}));
											}}
										/>
										<span>{t("isTaxIncluded")}</span>
									</label>
									<label className={styles.checkboxField}>
										<input
											type="checkbox"
											checked={header.showGtipInPdf}
											onChange={(e) => setHeader({ ...header, showGtipInPdf: e.target.checked })}
										/>
										<span>{t("showGtipInPdf")}</span>
									</label>
									<label className={styles.checkboxField}>
										<input
											type="checkbox"
											checked={lines.length > 0 && lines.every((l) => l.isCustomDesign)}
											onChange={(e) => {
												const checked = e.target.checked;
												setLines((prev) => prev.map((l) => ({ ...l, isCustomDesign: checked })));
											}}
										/>
										<span>{t("allCustomDesign")}</span>
									</label>
								</div>
								<FormField label={t("buyerExtraInfo")}>
									<textarea
										className={styles.textarea}
										style={{ height: 140 }}
										placeholder={t("buyerExtraInfoPlaceholder")}
										value={header.buyerExtraInfo}
										onChange={(e) => setHeader({ ...header, buyerExtraInfo: e.target.value })}
									/>
								</FormField>
								<FormField label={t("attachments")}>
									<div className={styles.attachmentsDropzoneWrap}>
										<FileDropzone files={pendingFiles} onChange={setPendingFiles} />
									</div>
									{isEdit && (existingAttachments.data?.items.length ?? 0) > 0 && (
										<div className={styles.existingAttachments}>
											{existingAttachments
												.data!.items.filter((item) => !isRemovedAttachment(item))
												.map((item) => (
													<div key={item.index} className={styles.existingAttachmentLink}>
														<a
															href={pendingOfferAttachmentDownloadUrl(
																editId as string,
																item.index,
																{
																	inline: true,
																},
															)}
															target="_blank"
															rel="noreferrer"
															className={styles.existingAttachmentName}
														>
															{item.name}
														</a>
														<a
															href={pendingOfferAttachmentDownloadUrl(editId as string, item.index)}
															aria-label={t("download")}
															className={styles.existingAttachmentDownload}
														>
															<Download size={14} />
														</a>
														<button
															type="button"
															aria-label={t("remove")}
															className={styles.existingAttachmentRemove}
															onClick={() => setRemovedAttachments((prev) => [...prev, item])}
														>
															<X size={14} />
														</button>
													</div>
												))}
										</div>
									)}
								</FormField>
							</div>
						</Card>

						<Card header={t("terms")}>
							<div className={styles.termsLangRow}>
								<button
									type="button"
									className={[styles.langButton, termsLang === "tr" && styles.langButtonActive]
										.filter(Boolean)
										.join(" ")}
									onClick={() => setTermsLang("tr")}
								>
									TR
								</button>
								<button
									type="button"
									className={[styles.langButton, termsLang === "en" && styles.langButtonActive]
										.filter(Boolean)
										.join(" ")}
									onClick={() => setTermsLang("en")}
								>
									EN
								</button>
							</div>
							<div className={styles.formGrid}>
								<FormField label={t("termsCommissioning")}>
									<textarea
										className={styles.textarea}
										rows={3}
										value={termsI18n.commissioning?.[termsLang] ?? ""}
										onChange={(e) => setTermValue("commissioning", e.target.value)}
									/>
								</FormField>
								<FormField label={t("termsAssembly")}>
									<textarea
										className={styles.textarea}
										rows={3}
										value={termsI18n.assembly?.[termsLang] ?? ""}
										onChange={(e) => setTermValue("assembly", e.target.value)}
									/>
								</FormField>
								<FormField label={t("termsVat")}>
									<textarea
										className={styles.textarea}
										rows={3}
										value={termsI18n.vat?.[termsLang] ?? ""}
										readOnly
										disabled
									/>
								</FormField>
								<FormField label={t("termsQuality")}>
									<textarea
										className={styles.textarea}
										rows={3}
										value={termsI18n.quality?.[termsLang] ?? ""}
										onChange={(e) => setTermValue("quality", e.target.value)}
									/>
								</FormField>
								<FormField label={t("termsValidity")}>
									<textarea
										className={styles.textarea}
										rows={3}
										value={termsI18n.validity?.[termsLang] ?? ""}
										readOnly
										disabled
									/>
								</FormField>
								<FormField label={t("termsWarranty")}>
									<textarea
										className={styles.textarea}
										rows={3}
										value={termsI18n.warranty?.[termsLang] ?? ""}
										onChange={(e) => setTermValue("warranty", e.target.value)}
									/>
								</FormField>
								<FormField label={t("termsPayment")}>
									<textarea
										className={styles.textarea}
										rows={3}
										value={termsI18n.payment?.[termsLang] ?? ""}
										onChange={(e) => setTermValue("payment", e.target.value)}
									/>
								</FormField>
								<FormField label={t("termsDelivery")}>
									<textarea
										className={styles.textarea}
										rows={3}
										value={termsI18n.delivery?.[termsLang] ?? ""}
										onChange={(e) => setTermValue("delivery", e.target.value)}
									/>
								</FormField>
								<FormField label={t("termsPaymentMethod")}>
									<textarea
										className={styles.textarea}
										rows={3}
										value={termsI18n.paymentMethod?.[termsLang] ?? ""}
										onChange={(e) => setTermValue("paymentMethod", e.target.value)}
									/>
								</FormField>
							</div>
						</Card>
					</>
				)}

				<Card header={t("lineItems")}>
					{alternativeGroups.map((group, groupIdx) => (
						<div key={group} className={styles.alternativeBlock}>
							<div className={styles.alternativeHeader}>
								<span className={styles.alternativeTitle}>
									{!isOrder && `${t("alternative")} ${groupIdx + 1}`}
								</span>
								<div className={styles.alternativeCurrency}>
									<FormField label={t("currency")}>
										<Select
											options={CURRENCY_OPTIONS}
											value={currencyOfGroup(group)}
											onChange={(e) => changeGroupCurrency(group, e.target.value)}
											disabled={isOrder}
										/>
									</FormField>
									<FormField label={t("rateDate")}>
										<Input
											type="date"
											value={groupRateDate[group] ?? header.offerDate ?? ""}
											onChange={(e) => void fetchGroupRates(group, e.target.value)}
										/>
									</FormField>
									{groupRateDate[group] &&
										(Object.keys(groupRates[group] ?? {}).length > 0 ? (
											<span className={styles.rateSummary}>
												{["USD", "EUR", "GBP"]
													.filter((c) => groupRates[group]?.[c])
													.map((c) => `${c} ${formatAmount(groupRates[group]![c]!)}`)
													.join(" · ")}
											</span>
										) : (
											<span className={styles.priceWarning}>⚠ {t("noRatesForDate")}</span>
										))}
								</div>
								{alternativeGroups.length > 1 && (
									<Button variant="ghost" size="sm" onClick={() => removeAlternative(group)}>
										<Minus size={16} /> {t("alternative")}
									</Button>
								)}
							</div>
							{lines.map((line, idx) =>
								line.alternativeGroup !== group ? null : line.isSection ? (
									<div key={idx} className={`${styles.lineCard} ${styles.sectionCard}`}>
										<div className={styles.lineHeader}>
											<span className={styles.lineBadge}>{idx + 1}</span>
											<span className={styles.sectionBadge}>{t("sectionLine")}</span>
											<Button
												variant="ghost"
												size="sm"
												className={styles.removeLineBtn}
												onClick={() => removeLine(idx)}
											>
												<Minus size={16} />
											</Button>
										</div>

										<div className={styles.sectionPresets}>
											{SECTION_PRESETS.map((preset) => (
												<button
													type="button"
													key={preset}
													className={[
														styles.priceInfoItem,
														line.displayDescription === preset && styles.priceInfoItemActive,
													]
														.filter(Boolean)
														.join(" ")}
													onClick={() => setSectionTitle(idx, preset)}
												>
													{preset}
												</button>
											))}
										</div>
										<FormField label={t("sectionTitle")}>
											<Input
												value={line.displayDescription}
												placeholder={SECTION_PRESETS[0]}
												onChange={(e) => setSectionTitle(idx, e.target.value)}
											/>
										</FormField>

										<div className={styles.sectionItems}>
											<span className={styles.sectionLabel}>{t("sectionItems")}</span>
											{line.sectionItems.map((item, itemIdx) => (
												<div key={itemIdx} className={styles.sectionItemRow}>
													<FormField label={t("itemDescription")}>
														<textarea
															className={inputStyles.input}
															rows={2}
															value={item.name}
															onChange={(e) =>
																updateSectionItem(idx, itemIdx, "name", e.target.value)
															}
														/>
													</FormField>
													<FormField label={t("quantity")}>
														<Input
															type="number"
															min="0"
															value={item.quantity}
															onChange={(e) =>
																updateSectionItem(idx, itemIdx, "quantity", e.target.value)
															}
														/>
													</FormField>
													<FormField label={t("unitPrice")}>
														<Input
															type="number"
															min="0"
															value={item.unitPrice}
															onChange={(e) =>
																updateSectionItem(idx, itemIdx, "unitPrice", e.target.value)
															}
														/>
													</FormField>
													<div className={styles.sectionItemTotal}>
														{formatAmount(
															(Number(item.quantity) || 0) * (Number(item.unitPrice) || 0),
														)}{" "}
														{currencyOfGroup(line.alternativeGroup)}
													</div>
													<Button
														variant="ghost"
														size="sm"
														onClick={() => removeSectionItem(idx, itemIdx)}
													>
														<Minus size={16} />
													</Button>
												</div>
											))}
											<Button variant="secondary" size="sm" onClick={() => addSectionItem(idx)}>
												<Plus size={16} /> {t("sectionAddItem")}
											</Button>
										</div>

										<div className={styles.numbersRow}>
											<FormField label={t("quantity")}>
												<Input
													type="number"
													value={line.quantity}
													onChange={(e) => updateLine(idx, "quantity", e.target.value)}
												/>
											</FormField>
											<FormField label={t("unit")}>
												<Select
													options={[
														{ value: "", label: "—" },
														...unitOptions,
														...(line.unit && !unitOptions.some((o) => o.value === line.unit)
															? [{ value: line.unit, label: line.unit }]
															: []),
													]}
													value={line.unit}
													onChange={(e) => updateLine(idx, "unit", e.target.value)}
												/>
											</FormField>
											<FormField label={t("vatRate")}>
												<SearchableSelect
													value={line.vatGroup}
													options={vatGroupOptions}
													initialOption={
														line.vatGroup && !vatGroups.some((v) => v.code === line.vatGroup)
															? { value: line.vatGroup, label: line.vatGroup }
															: undefined
													}
													placeholder={t("search")}
													clearable
													onChange={(code) => {
														const g = vatGroups.find((v) => v.code === code);
														setLines((prev) => {
															const next = [...prev];
															if (next[idx])
																next[idx] = {
																	...next[idx],
																	vatGroup: code,
																	...(g ? { vatRate: String(g.rate) } : {}),
																} as LineItem;
															return next;
														});
													}}
												/>
											</FormField>
										</div>

										{!isOrder && (
											<FormField label={t("commitmentDuration")}>
												<div className={styles.durationRow}>
													<Input
														type="text"
														value={line.commitmentValue}
														placeholder={header.commitmentValue || t("commitmentDuration")}
														onChange={(e) => updateLine(idx, "commitmentValue", e.target.value)}
														disabled={header.offerOrderDeliveryType === STOCK_DELIVERY_TYPE}
														style={{ width: "auto", flexShrink: 0 }}
													/>
													<Select
														options={[
															{ value: "day", label: t("unitDay") },
															{ value: "week", label: t("unitWeek") },
															{ value: "month", label: t("unitMonth") },
														]}
														value={line.commitmentUnit}
														onChange={(e) => updateLine(idx, "commitmentUnit", e.target.value)}
														disabled={header.offerOrderDeliveryType === STOCK_DELIVERY_TYPE}
														style={{ width: 110, flexShrink: 0 }}
													/>
												</div>
											</FormField>
										)}
										<div className={styles.lineFooter}>
											<div className={styles.lineFlags}>
												{!isOrder && (
													<>
														<label>
															<input
																type="checkbox"
																checked={line.showInPdf}
																onChange={() => toggleLineFlag(idx, "showInPdf")}
															/>
															{t("showInPdf")}
														</label>
														<label>
															<input
																type="checkbox"
																checked={line.showPriceInPdf}
																onChange={() => toggleLineFlag(idx, "showPriceInPdf")}
															/>
															{t("showPriceInPdf")}
														</label>
													</>
												)}
											</div>
											<div className={styles.lineTotalWrap}>
												<span className={styles.lineTotalLabel}>{t("lineTotal")}</span>
												<span className={styles.lineTotal}>
													{formatAmount(displayLineTotal(line, idx))}{" "}
													{currencyOfGroup(line.alternativeGroup)}
												</span>
											</div>
										</div>
									</div>
								) : (
									<div key={idx} className={styles.lineCard}>
										<div className={styles.lineHeader}>
											<span className={styles.lineBadge}>{idx + 1}</span>
											{lines.length > 1 && (
												<Button
													variant="ghost"
													size="sm"
													className={styles.removeLineBtn}
													onClick={() => removeLine(idx)}
												>
													<Minus size={16} />
												</Button>
											)}
										</div>

										{/* Ürün seçimi */}
										<div className={styles.lineSection}>
											<span className={styles.sectionLabel}>{t("productSelection")}</span>
											<div className={styles.selectRow}>
												<FormField label={t("lineSource")}>
													<Select
														options={[
															{ value: "priceList", label: t("sourcePriceList") },
															{ value: "sap", label: t("sourceSapItem") },
														]}
														value={lineSource[idx] ?? "priceList"}
														onChange={(e) =>
															changeLineSource(idx, e.target.value as "priceList" | "sap")
														}
													/>
												</FormField>
												{(lineSource[idx] ?? "priceList") === "sap" ? (
													<FormField label={t("sourceSapItem")}>
														<SearchableSelect
															key={`src-sap-${idx}`}
															value={directSapKey[idx] ?? ""}
															onChange={(val, option) =>
																handleSelectDirectSapItem(idx, val, option)
															}
															loadOptions={loadDirectSapItems}
															initialOption={isOrder ? sapItemInitialOptions[idx] : undefined}
															placeholder={t("selectStockItem")}
															clearable
														/>
													</FormField>
												) : (
													<FormField label={t("priceListModel")}>
														<SearchableSelect
															key={`src-pl-${idx}`}
															value={selectedModelKey[idx] ?? ""}
															onChange={(val) => void handleSelectModel(idx, val)}
															loadOptions={loadPriceModels}
															initialOption={modelInitialOptions[idx]}
															placeholder={t("selectPriceListItem")}
															clearable
														/>
													</FormField>
												)}
												{(lineSource[idx] ?? "priceList") === "priceList" &&
													selectedModelKey[idx] && (
														<FormField label={t("sapItem")}>
															<SearchableSelect
																key={`sap-${idx}-${selectedModelKey[idx] ?? ""}`}
																value={selectedSapKey[idx] ?? ""}
																onChange={(val, option) => handleSelectSapItem(idx, val, option)}
																loadOptions={(q) =>
																	loadSapItemsForModel(selectedModelKey[idx] ?? "", q)
																}
																initialOption={sapItemInitialOptions[idx]}
																placeholder={t("selectStockItem")}
																clearable
															/>
														</FormField>
													)}
											</div>
											{(() => {
												const hs = deriveGtip({
													code: line.itemCode,
													name: line.displayDescription || line.itemDescription,
													model: line.priceListModel || selectedModelKey[idx] || null,
												});
												return hs ? (
													<div className={styles.hsCodeRow}>
														<span className={styles.hsCodeLabel}>{t("hsCode")}</span> {hs}
													</div>
												) : null;
											})()}
											{(lineInfo[idx]?.length ?? 0) > 0 && (
												<div className={styles.priceInfo}>
													<span className={styles.priceInfoHint}>{t("clickToAddPrice")}</span>
													{lineInfo[idx]!.map((info) => {
														const applied = appliedInfo[idx]?.[info.label] ?? false;
														const grp = lines[idx]?.alternativeGroup ?? 0;
														const conv = convertPrice(info.price, info.currency ?? null, grp);
														const target = currencyOfGroup(grp);
														return (
															<button
																type="button"
																key={info.label}
																className={[
																	styles.priceInfoItem,
																	applied && styles.priceInfoItemActive,
																]
																	.filter(Boolean)
																	.join(" ")}
																onClick={() => toggleInfoPrice(idx, info)}
															>
																{applied ? "− " : "+ "}
																{info.label}:{" "}
																{formatAmount(conv.converted ? conv.value : info.price)}
																{conv.converted
																	? ` ${target}`
																	: info.currency
																		? ` ${info.currency}`
																		: ""}
															</button>
														);
													})}
												</div>
											)}
											{lineWarning[idx] && (
												<span className={styles.priceWarning}>⚠ {lineWarning[idx]}</span>
											)}
											{!isOrder && line.stockItemId && (
												<StockReservationPanel
													availability={availabilityByItem.get(line.stockItemId)}
													isLoading={stockAvailability.isLoading}
													offerId={editId}
													alternativeGroup={line.alternativeGroup}
													lineQuantity={Number(line.quantity) || 0}
												/>
											)}
											{Object.keys(linePanels[idx] ?? {}).length > 0 && (
												<div className={styles.panelRow}>
													{["ATS", "Change Over ATS", "CB"]
														.filter((g) => linePanels[idx]?.[g])
														.map((group) => {
															const opts = linePanels[idx]![group]!;
															const cur = linePrices[idx]?.currency ?? "";
															return (
																<Fragment key={group}>
																	<div className={styles.panelGroup}>
																		<label className={styles.panelCheck}>
																			<input
																				type="checkbox"
																				checked={panelChecked[idx]?.[group] ?? false}
																				onChange={() => togglePanelGroup(idx, group)}
																			/>
																			{group}
																		</label>
																		{panelChecked[idx]?.[group] && (
																			<>
																				<Select
																					options={[
																						{ value: "", label: t("selectPrice") },
																						...Object.entries(opts).map(([opt, price]) => ({
																							value: opt,
																							label: `${opt} — ${formatAmount(price)}${cur ? ` ${cur}` : ""}`,
																						})),
																						...(isOrder &&
																						panelSelected[idx]?.[group] &&
																						opts[panelSelected[idx]![group]!] === undefined
																							? [
																									{
																										value: panelSelected[idx]![group]!,
																										label: panelSelected[idx]![group]!,
																									},
																								]
																							: []),
																					]}
																					value={panelSelected[idx]?.[group] ?? ""}
																					onChange={(e) =>
																						selectPanelOption(idx, group, e.target.value)
																					}
																				/>
																				<Input
																					type="number"
																					min="1"
																					className={styles.panelQty}
																					title={t("quantity")}
																					placeholder={t("quantity")}
																					value={panelQty[idx]?.[group] ?? "1"}
																					onChange={(e) =>
																						setPanelQtyValue(idx, group, e.target.value)
																					}
																				/>
																				<label className={styles.panelCheck}>
																					<input
																						type="checkbox"
																						checked={!!panelSeparate[idx]?.[group]}
																						onChange={() => togglePanelSeparate(idx, group)}
																					/>
																					{t("billSeparately")}
																				</label>
																				{panelSeparate[idx]?.[group] && (
																					<>
																						<div className={styles.panelSeparateItem}>
																							<SearchableSelect
																								key={`sep-${idx}-${group}`}
																								value={panelSeparate[idx]![group]!.stockItemId}
																								onChange={(val, option) =>
																									selectSeparateItem(idx, group, val, option)
																								}
																								loadOptions={
																									panelItemLoaders[group] ?? loadDirectSapItems
																								}
																								initialOption={
																									panelSeparate[idx]![group]!.stockItemId
																										? {
																												value:
																													panelSeparate[idx]![group]!.stockItemId,
																												label: `${panelSeparate[idx]![group]!.itemCode} — ${panelSeparate[idx]![group]!.itemDescription}`,
																											}
																										: undefined
																								}
																								placeholder={t("selectStockItem")}
																								clearable
																							/>
																						</div>
																					</>
																				)}
																				{!isOrder && !panelSeparate[idx]?.[group] && (
																					<>
																						<label className={styles.panelCheck}>
																							<input
																								type="checkbox"
																								checked={panelShow[idx]?.[group] ?? true}
																								onChange={() => togglePanelShow(idx, group)}
																							/>
																							{t("showInOffer")}
																						</label>
																						<label className={styles.panelCheck}>
																							<input
																								type="checkbox"
																								checked={panelPriced[idx]?.[group] ?? false}
																								disabled={!(panelShow[idx]?.[group] ?? true)}
																								onChange={() => togglePanelPriced(idx, group)}
																							/>
																							{t("showPrice")}
																						</label>
																					</>
																				)}
																			</>
																		)}
																	</div>
																</Fragment>
															);
														})}
												</div>
											)}
										</div>

										{/* Açıklamalar */}
										<div className={styles.fieldRow}>
											<FormField label={t("itemCode")}>
												<Input
													value={line.itemCode}
													onChange={(e) => updateLine(idx, "itemCode", e.target.value)}
												/>
											</FormField>
											<FormField label={t("itemDescription")}>
												<Input
													value={line.itemDescription}
													onChange={(e) => updateLine(idx, "itemDescription", e.target.value)}
												/>
											</FormField>
											<FormField label={t("displayDescription")}>
												<Input
													value={line.displayDescription}
													onChange={(e) => updateLine(idx, "displayDescription", e.target.value)}
												/>
											</FormField>
										</div>

										{/* Miktar & fiyat */}
										<div className={styles.numbersRow}>
											<FormField label={t("quantity")}>
												<Input
													type="number"
													value={line.quantity}
													onChange={(e) => updateLine(idx, "quantity", e.target.value)}
												/>
											</FormField>
											<FormField label={t("unit")}>
												<Select
													options={[
														{ value: "", label: "—" },
														...unitOptions,
														...(line.unit && !unitOptions.some((o) => o.value === line.unit)
															? [{ value: line.unit, label: line.unit }]
															: []),
													]}
													value={line.unit}
													onChange={(e) => updateLine(idx, "unit", e.target.value)}
												/>
											</FormField>
											<FormField
												label={
													isOrder
														? t("unitPrice")
														: isManualListPrice(idx)
															? `${t("listPrice")} (${t("manualEntry")})`
															: t("listPrice")
												}
											>
												{isOrder || isManualListPrice(idx) ? (
													<Input
														type="number"
														value={line.unitPrice}
														onChange={(e) => setListPrice(idx, e.target.value)}
													/>
												) : (
													<Input type="number" value={line.unitPrice} disabled readOnly />
												)}
											</FormField>
											{!isOrder && (
												<FormField label={t("givenPrice")}>
													<Input
														type="number"
														value={line.givenUnitPrice}
														placeholder={line.unitPrice || undefined}
														onChange={(e) => setGivenPrice(idx, e.target.value)}
													/>
												</FormField>
											)}
											<FormField label={t("vatRate")}>
												<SearchableSelect
													value={line.vatGroup}
													options={vatGroupOptions}
													initialOption={
														line.vatGroup && !vatGroups.some((v) => v.code === line.vatGroup)
															? { value: line.vatGroup, label: line.vatGroup }
															: undefined
													}
													placeholder={t("search")}
													clearable
													onChange={(code) => {
														const g = vatGroups.find((v) => v.code === code);
														setLines((prev) => {
															const next = [...prev];
															if (next[idx])
																next[idx] = {
																	...next[idx],
																	vatGroup: code,
																	...(g ? { vatRate: String(g.rate) } : {}),
																} as LineItem;
															return next;
														});
													}}
												/>
											</FormField>
											<FormField label={t("discountPercent")}>
												<Input
													type="number"
													step="any"
													value={line.discountPercent}
													onChange={(e) => setDiscountPercent(idx, e.target.value)}
												/>
											</FormField>
										</div>

										<div className={styles.fieldRow}>
											<FormField label={t("lineDetail")}>
												<textarea
													className={styles.textarea}
													rows={2}
													value={line.lineDetail}
													onChange={(e) => updateLine(idx, "lineDetail", e.target.value)}
												/>
											</FormField>
											<FormField
												label={`${t("factorySpecialNotes")} (${line.factorySpecialNotes?.length ?? 0}/254)`}
											>
												<textarea
													className={styles.textarea}
													rows={2}
													maxLength={254}
													value={line.factorySpecialNotes}
													onChange={(e) => updateLine(idx, "factorySpecialNotes", e.target.value)}
												/>
											</FormField>
											<FormField
												label={`${t("lineGeneralNotes")} (${line.generalNotes?.length ?? 0}/150)`}
											>
												<textarea
													className={styles.textarea}
													rows={2}
													maxLength={150}
													value={line.generalNotes}
													onChange={(e) => updateLine(idx, "generalNotes", e.target.value)}
												/>
											</FormField>
										</div>
										{isOrder && (
											<div className={styles.fieldRow}>
												<FormField label={t("warehouse")}>
													<Select
														options={[
															{ value: "", label: "—" },
															...orderWarehouses.map((w) => ({
																value: w.id,
																label: `${w.code} - ${w.name}`,
															})),
														]}
														value={line.warehouseId}
														onChange={(e) => updateLine(idx, "warehouseId", e.target.value)}
													/>
												</FormField>
												<FormField label={t("atsDescription")}>
													<textarea
														className={styles.textarea}
														rows={2}
														value={line.atsDescription}
														onChange={(e) => updateLine(idx, "atsDescription", e.target.value)}
													/>
												</FormField>
												<FormField label={t("tmsDescription")}>
													<textarea
														className={styles.textarea}
														rows={2}
														value={line.tmsDescription}
														onChange={(e) => updateLine(idx, "tmsDescription", e.target.value)}
													/>
												</FormField>
											</div>
										)}
										{!isOrder && (
											<FormField label={t("commitmentDuration")}>
												<div className={styles.durationRow}>
													<Input
														type="text"
														value={line.commitmentValue}
														placeholder={header.commitmentValue || t("commitmentDuration")}
														onChange={(e) => updateLine(idx, "commitmentValue", e.target.value)}
														disabled={header.offerOrderDeliveryType === STOCK_DELIVERY_TYPE}
														style={{ width: "auto", flexShrink: 0 }}
													/>
													<Select
														options={[
															{ value: "day", label: t("unitDay") },
															{ value: "week", label: t("unitWeek") },
															{ value: "month", label: t("unitMonth") },
														]}
														value={line.commitmentUnit}
														onChange={(e) => updateLine(idx, "commitmentUnit", e.target.value)}
														disabled={header.offerOrderDeliveryType === STOCK_DELIVERY_TYPE}
														style={{ width: 110, flexShrink: 0 }}
													/>
												</div>
											</FormField>
										)}
										<div className={styles.lineFooter}>
											<div className={styles.lineFlags}>
												{!isOrder && (
													<>
														<label>
															<input
																type="checkbox"
																checked={line.showInPdf}
																onChange={() => toggleLineFlag(idx, "showInPdf")}
															/>
															{t("showInPdf")}
														</label>
														<label>
															<input
																type="checkbox"
																checked={line.showPriceInPdf}
																onChange={() => toggleLineFlag(idx, "showPriceInPdf")}
															/>
															{t("showPriceInPdf")}
														</label>
														<label>
															<input
																type="checkbox"
																checked={line.showDiscountInPdf}
																onChange={() => toggleLineFlag(idx, "showDiscountInPdf")}
															/>
															{t("showDiscountInPdf")}
														</label>
														<label>
															<input
																type="checkbox"
																checked={line.sendDatasheetInEmail}
																onChange={() => toggleLineFlag(idx, "sendDatasheetInEmail")}
															/>
															{t("sendDatasheetInEmail")}
														</label>
													</>
												)}
												<label>
													<input
														type="checkbox"
														checked={line.isCustomDesign}
														onChange={() => toggleLineFlag(idx, "isCustomDesign")}
													/>
													{t("isCustomDesign")}
												</label>
											</div>
											<div className={styles.lineTotalWrap}>
												<span className={styles.lineTotalLabel}>{t("lineTotal")}</span>
												<span className={styles.lineTotal}>
													{formatAmount(displayLineTotal(line, idx))}{" "}
													{currencyOfGroup(line.alternativeGroup)}
												</span>
											</div>
										</div>
									</div>
								),
							)}
							<div className={styles.addLineRow}>
								<Button
									variant="secondary"
									className={styles.addLineBtn}
									onClick={() => addLine(group)}
								>
									<Plus size={16} />
								</Button>
								<Button variant="secondary" onClick={() => addSection(group)}>
									<Plus size={16} /> {t("addSection")}
								</Button>
							</div>
							<div className={styles.alternativeSubtotal}>
								<span>{t("subtotal")}</span>
								<span>
									{formatAmount(groupTotals(group).subtotal)} {currencyOfGroup(group)}
								</span>
							</div>
						</div>
					))}
					{!isOrder && (
						<Button variant="secondary" onClick={addAlternative}>
							<Plus size={16} /> {t("alternative")}
						</Button>
					)}
				</Card>

				<Card>
					{alternativeGroups.length === 1 && (
						<div className={styles.totals}>
							<div className={styles.totalRow}>
								<span>{t("subtotal")}</span>
								<span>
									{formatAmount(subtotal)} {offerCurrency}
								</span>
							</div>
							<div className={styles.totalRow}>
								<span>{t("vatTotal")}</span>
								<span>
									{formatAmount(vatTotal)} {offerCurrency}
								</span>
							</div>
							<div className={[styles.totalRow, styles.grandTotal].join(" ")}>
								<span>{t("total")}</span>
								<span>
									{formatAmount(total)} {offerCurrency}
								</span>
							</div>
						</div>
					)}
					<div className={styles.submitRow}>
						<Button
							onClick={submit}
							isLoading={
								isOrderCopy
									? createOrder.isPending
									: isOrder
										? updateOrder.isPending
										: isEdit
											? updateOffer.isPending
											: createOffer.isPending
							}
						>
							{isEdit && !isOrderCopy ? t("save") : t("create")}
						</Button>
					</div>
				</Card>
			</div>

			<Modal
				isOpen={personModalOpen}
				onClose={() => setPersonModalOpen(false)}
				title={t("addPerson")}
				closeOnOverlayClick={false}
			>
				{personError && <div className={styles.formError}>{personError}</div>}
				<FormField label={t("firstName")} required>
					<Input
						value={newPerson.firstName}
						onChange={(e) => setNewPerson({ ...newPerson, firstName: e.target.value })}
						autoFocus
					/>
				</FormField>
				<FormField label={t("lastName")} required>
					<Input
						value={newPerson.lastName}
						onChange={(e) => setNewPerson({ ...newPerson, lastName: e.target.value })}
					/>
				</FormField>
				<FormField label={t("phone")}>
					<Input
						value={newPerson.phone}
						onChange={(e) => setNewPerson({ ...newPerson, phone: e.target.value })}
					/>
				</FormField>
				<FormField label={t("email")}>
					<Input
						type="email"
						value={newPerson.email}
						onChange={(e) => setNewPerson({ ...newPerson, email: e.target.value })}
					/>
				</FormField>
				<div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
					<Button variant="ghost" onClick={() => setPersonModalOpen(false)}>
						{t("cancel")}
					</Button>
					<Button onClick={saveNewPerson} isLoading={personSaving}>
						{t("save")}
					</Button>
				</div>
			</Modal>
		</div>
	);
}
